import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import {
  AlertController,
  LoadingController,
  ModalController,
} from '@ionic/angular';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
  firstValueFrom,
  Subject,
} from 'rxjs';
import * as L from 'leaflet';
import 'leaflet-arrowheads';
import { TranslateService } from '@ngx-translate/core';
//Services
import { ToastService } from 'src/app/services/toasts.service';
import { HttpResponsesService } from 'src/app/services/httpResponses.service';
import { MapLayerCategoryEnum } from 'src/app/components/leaflet-map/models/map-layer-category.enum';
//Components
import { LeafletMapComponent } from 'src/app/components/leaflet-map/leaflet-map.component';
import { LeftPanelTreeComponent } from '../left-panel-tree/left-panel-tree.component';
import { SelectRegionAreaComponent } from '../select-region-area/select-region-area.component';
import { CheckNearInfrastructuresComponent } from '../../check-near-infrastructures/check-near-infrastructures.component';
import { CheckNearUrbanAreasComponent } from '../../check-near-urban-areas/check-near-urban-areas.component';
import { CheckNearTracksComponent } from '../../check-near-tracks/check-near-tracks.component';
//Models
import {
  User,
  Infrastructure,
  Location,
  Region,
  UrbanAreasBasicInfo,
  AutopilotCommand,
  AutopilotTrajectory,
  TrajectoryStage,
  TrackNetworkSegment,
  Directory,
  PlanningItem,
  AutopilotSpeedUnitsStringEnum,
  AutopilotAltitudeUnitsStringEnum,
  MultipleApiResponse,
} from '@mocca-technologies/model';
//Enums
import {
  AutopilotCommandEnum,
  AutopilotTrajectoryTypeEnum,
  AutopilotSpeedTypeEnum,
  AutopilotSpeedUnitsEnum,
  AutopilotTrajectoryDetailEnum,
  AutopilotTrajectoryStatusEnum,
  AutopilotAltitudeTypeEnum,
  AutopilotAltitudeUnitsEnum,
  GenericObjectTypeEnum,
  toInteger,
} from '@mocca-technologies/model';
//Utils
import {
  TrajectoryUtils,
  TrajectoryState,
  Colors,
} from '@mocca-technologies/model';
import { Icons } from 'src/app/components/trajectory-creation/icons.model';
import { droneIcon } from 'src/assets/icons';
import { AutopilotService } from 'src/app/services/ap-operations.service';

@Component({
  selector: 'app-leaflet-create-trajectory',
  templateUrl: './leaflet-create-trajectory.component.html',
  styleUrls: ['./leaflet-create-trajectory.component.scss'],
})
export class LeafletCreateTrajectoryComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  // Leaflet event handlers (for cleanup)
  private _leafletMap: L.Map;

  droneIcon = droneIcon;

  @ViewChild('leafletMapCmp') private leafletMapCmp: LeafletMapComponent;
  @ViewChild('trajectoryTree') trajectoryTree: LeftPanelTreeComponent;
  @ViewChild('checkNearInfrastructuresComponent')
  checkNearInfrasComponent: CheckNearInfrastructuresComponent;
  @ViewChild('checkNearUrbanAreasComponent')
  checkNearUrbanAreasComponent: CheckNearUrbanAreasComponent;
  @ViewChild('checkNearTracksComponent')
  checkNearTracksComponent: CheckNearTracksComponent;

  user = JSON.parse(localStorage.getItem('user')) as User;

  isLoaded = false;
  loadedTrajectory: AutopilotTrajectory;

  profile: string = 'auth';

  icons = new Icons();
  colors = new Colors();
  trajectoryUtils = new TrajectoryUtils();

  showTrajectoryCard: boolean = true;
  showHelpCard: boolean = true;

  private loadingEl?: HTMLIonLoadingElement;

  /**
   * Boolean to control States for the trajectory creation ----------------------------------------------------------------------------------------------------
   */
  isWaypointEnabled: boolean = false;
  isRegionEnabled: boolean = false;
  isTakeoffEnabled: boolean = false;
  isLandEnabled: boolean = false;
  isForkEnabled: boolean = false;
  isJoinEnabled: boolean = false;

  // isAddingFork is used to control when the user is adding a fork track to a fork with already one fork track
  isAddingFork: boolean = false;
  /*  isFirstFork is used to control when the user is adding the first fork track to a fork, so when the user has added a ForkRoute Waypoint
      to get out of this firstFork state, the user has to add a JoinRoute Waypoint*/
  isFirstFork: boolean = false;

  get isUndoEnabled(): boolean {
    switch (this.currentStage) {
      case 'Departure':
        return this.currentBranch != null &&
          (this.waypointsDeparture.get(this.currentBranch)?.size ?? 0) > 0;
      case 'Mission':
        return this.waypointsMission.size > 0;
      case 'Arrival':
        return this.currentBranch != null &&
          (this.waypointsArrival.get(this.currentBranch)?.size ?? 0) > 0;
      default:
        return false;
    }
  }
  //------------------------------------------------------------------------------------------------------------------------------------------------------------

  /**
   * Vectors and layers to store the different waypoints, tracks and regions of the trajectory-------------------------------------------------------------------
   */
  openedMarker = null;
  markersLayer = new L.LayerGroup<L.Marker>();
  tracksLayer = new L.LayerGroup<L.Polyline>();
  polygonLayers = new L.LayerGroup<L.Polygon>();

  waypointsDeparture = new Map<string, Map<AutopilotCommand, L.Marker>>();
  waypointsTracksDeparture = new Map<AutopilotCommand, L.Polyline>();
  regionsDeparture = new Map<AutopilotCommand, L.Polygon>();

  waypointsMission = new Map<AutopilotCommand, L.Marker>();
  waypointsTracksMission = new Map<AutopilotCommand, L.Polyline>();
  regionsMission = new Map<AutopilotCommand, L.Polygon>();

  waypointsArrival = new Map<string, Map<AutopilotCommand, L.Marker>>();
  waypointsTracksArrival = new Map<AutopilotCommand, L.Polyline>();
  regionsArrival = new Map<AutopilotCommand, L.Polygon>();

  tracksBetweenStages = new Map<string, L.Polyline>();
  regionPolygon: L.Polygon;
  region: Region;
  //------------------------------------------------------------------------------------------------------------------------------------------------------------

  /**
   * Current Stage of the trajectory, there are only three Departure, Mission and Arrival. Method to change the current stage------------------------------------
   */
  currentStage: string = TrajectoryStage.Departure;
  currentState: TrajectoryState = TrajectoryState.CreatingInitialTrack;
  currentBranch: string;

  currentFork: AutopilotCommand;
  currentForkLoading: AutopilotCommand;
  currentJoin: AutopilotCommand;
  lastWaypoint: AutopilotCommand;

  currentSelection: string;

  isDevel() {
    return this.profile === 'devel';
  }

  /**
   * This method is used to change the current stage of the trajectory, it first update the StageJoint tracks (dashed lines between stages)
   * after that it checks which is the next stage and changes the current branch depending if it is Departure or Arrival (null for mission).
   * This method is used to change stage automatically during the trajectory creation.
   * @param stage: new Stage to change to
   */
  changeStage(stage: string) {
    this.refreshStageJoints();
    if (stage === 'Departure' && this.currentBranch === null) {
      this.updateTree();
      // If we are changing to Departure we will select the last initial track added as the current branch
      this.trajectoryTree.tree.obtainArray().forEach((item: PlanningItem) => {
        if (item.name && item.name.includes('Track Initial')) {
          if (!this.currentBranch) this.currentBranch = item.name;
        }
      });
    }

    if (stage === 'Mission') {
      this.currentBranch = null;
    }

    if (stage === 'Arrival' && this.currentBranch === null) {
      this.updateTree();
      // If we are changing to Arrival we will select the last final track added as the current branch
      this.trajectoryTree.tree.obtainArray().forEach((item: PlanningItem) => {
        if (item.name && item.name.includes('Track Arrival')) {
          if (!this.currentBranch) this.currentBranch = item.name;
        }
      });
    }
    this.currentStage = stage;
  }

  /**
   * This method is used to change the current stage of the trajectory, it first update the StageJoint tracks (dashed lines between stages)
   * after that it checks which is the next stage and changes the stage and the state directly to Ininitial, InMission or InArrival.
   * This method is used to change stage manually from the buttons, and change stage to modify another part of the trajectory.
   * @param stage: new Stage to change to
   */
  changeStageDirectly(stage: string) {
    this.currentSelection = null;
    this.attenuateTrajectory();
    this.refreshStageJoints();
    switch (stage) {
      case 'Departure': {
        this.changeStage(stage);
        this.changeState(TrajectoryState.InInitialTrack);
        break;
      }
      case 'Mission': {
        this.changeStage(stage);
        this.changeState(TrajectoryState.InMission);
        break;
      }
      case 'Arrival': {
        this.changeStage(stage);
        this.changeState(TrajectoryState.InArrivalTrack);
        break;
      }
    }
    this.ref.detectChanges();
  }
  //------------------------------------------------------------------------------------------------------------------------------------------------------------

  /**
   * Default values of altitude speed and waypoint type for the initial values of a new waypoint and methods to change the values--------------------------------
   */
  getAltitude(): number {
    return this.trajectoryTree.defaultAltitude;
  }

  getSpeed(): number {
    return this.trajectoryTree.defaultSpeed;
  }

  currentWaypointType: AutopilotCommandEnum = AutopilotCommandEnum.SetWaypoint;
  getWaypointType(): AutopilotCommandEnum {
    return this.currentWaypointType;
  }

  /**
   *
   * @param waypointType
   */
  switchSelectedWaypointType(waypointType: string) {
    this.currentSelection = null;
    this.attenuateTrajectory();

    // IF JOIN ROUTE CHECK IF FORK HAS AT LEAST ONE WAYPOINT
    if (waypointType === 'JoinRoute') {
      switch (this.currentStage) {
        case 'Departure': {
          if (
            this.trajectoryTree.tree
              .searchByName(this.currentBranch)
              .data.items.find(
                (item) =>
                  item.name === 'Fork ' + (this.trajectoryTree.forkCounter - 1),
              ).items.length >= 1
          ) {
            this.changeWaypointType(AutopilotCommandEnum[waypointType]);
          } else {
            this.toastService.presentToast(
              this.translateService.instant('FP.TOAST.ADD_WAYPOINT'),
              'bottom',
              5000,
              '',
              'warning',
            );
          }
          break;
        }
        case 'Mission': {
          if (
            this.trajectoryTree.tree
              .searchByName(this.currentStage)
              .data.items.find(
                (item) =>
                  item.name === 'Fork ' + (this.trajectoryTree.forkCounter - 1),
              ).items.length >= 1
          ) {
            this.changeWaypointType(AutopilotCommandEnum[waypointType]);
          } else {
            this.toastService.presentToast(
              this.translateService.instant('FP.TOAST.ADD_WAYPOINT'),
              'bottom',
              5000,
              '',
              'warning',
            );
          }
          break;
        }
        case 'Arrival': {
          if (
            this.trajectoryTree.tree
              .searchByName(this.currentBranch)
              .data.items.find(
                (item) =>
                  item.name === 'Fork ' + (this.trajectoryTree.forkCounter - 1),
              ).items.length >= 1
          ) {
            this.changeWaypointType(AutopilotCommandEnum[waypointType]);
          } else {
            this.toastService.presentToast(
              this.translateService.instant('FP.TOAST.ADD_WAYPOINT'),
              'bottom',
              5000,
              '',
              'warning',
            );
          }
          break;
        }
      }
    } else {
      this.changeWaypointType(AutopilotCommandEnum[waypointType]);
    }
    this.ref.detectChanges();
  }

  changeWaypointType(waypointType: AutopilotCommandEnum) {
    if (
      !this.isDevel() &&
      this.currentStage === 'Arrival' &&
      waypointType !== 'Land'
    ) {
      this.changeStageDirectly('Mission');
      return;
    }

    this.currentWaypointType = waypointType;
    if (waypointType === 'MissionArea') {
      this.presentSelectRegionModal();
    }
    if (!this.isDevel() && waypointType === 'Land') {
      this.changeStage('Arrival');
    }
  }
  //------------------------------------------------------------------------------------------------------------------------------------------------------------

  /**
   * Leaflet variables to define everything needed in leaflet maps-----------------------------------------------------------------------------------------------
   */
  get leafletmap(): L.Map { return this._leafletMap ?? this.leafletMapCmp?.leafletmap; }
  private readonly DEFAULT_CENTER: L.LatLng = L.latLng(41.27640718208692, 1.9886429501751366);
  private _center: L.LatLng = this.DEFAULT_CENTER; //UPC Campus Castelldefels

  /**
   *
   * @param center
   * @param zoomValue
   */
  centerMap(center: L.LatLng, zoomValue?: number) {
    if (center && !isNaN(center.lat) && !isNaN(center.lng)) {
      if (this.leafletmap) {
        this.leafletmap.setView(
          center,
          zoomValue || this.leafletmap.getZoom(),
          { animate: true, duration: 0.5 },
        );
      } else {
        console.error('leafletmap is not initialized');
      }
    } else {
      console.error('Invalid center:', center);
    }
  }

  /**
   * Called when the embedded leaflet-map signals that its map is ready.
   */
  onMapReady(map: L.Map) {
    // Store the map reference directly — avoids relying on ViewChild timing.
    this._leafletMap = map;

    // Add trajectory layers directly to the map (timing-safe, no ViewChild needed).
    map.addLayer(this.markersLayer);
    map.addLayer(this.tracksLayer);
    map.addLayer(this.polygonLayers);

    // Layers are already added to map in addLayer calls above
    // No registration needed for the new LeafletMapComponent

    setTimeout(() => this.leafletmap.invalidateSize(true), 0);

    setTimeout(() => {
      this.leafletmap.invalidateSize(true);
    }, 500);

    this.centerMap(this._center, 12);

    this.changeState(TrajectoryState.CreatingInitialTrack);
    this.changeStage('Departure');

    this.trajectoryTree.isLoaded = this.isLoaded;
  }

  /** Private getters that proxy layer groups from the embedded leaflet-map component. */
  private get structuresLayerGroup() { return this.leafletMapCmp?.structuresLayerGroup; }
  private get innerZonesLayerGroup() { return this.leafletMapCmp?.innerZonesLayerGroup; }
  private get runwayLayerGroup() { return this.leafletMapCmp?.runwayLayerGroup; }
  private get protectionLowLayerGroup() { return this.leafletMapCmp?.protectionLowLayerGroup; }
  private get protectionHighLayerGroup() { return this.leafletMapCmp?.protectionHighLayerGroup; }
  private get ctrAtzLayerGroup() { return this.leafletMapCmp?.ctrAtzLayerGroup; }
  private get naturalParksLayerGroup() { return this.leafletMapCmp?.naturalParksLayerGroup; }
  private get harboursLayerGroup() { return this.leafletMapCmp?.harboursLayerGroup; }
  private get trackNetworkLayerGroup() { return this.leafletMapCmp?.trackNetworkLayerGroup; }
  private get urbanAreasLayerGroup() { return this.leafletMapCmp?.urbanAreasLayerGroup; }
  private get urbanAreasRegistryLayerGroup() { return this.leafletMapCmp?.urbanAreasRegistryLayerGroup; }
  private get otherLayerGroup() { return this.leafletMapCmp?.otherLayerGroup; }

  //----------------------------------------------------------------------------------
  /**
   * Variables to manage the check near infrastructures component
   */
  searchRadiusCircle: L.Circle;

  infrastructureMarkers: L.Marker[] = [];

  /**
   * Variables to manage fab buttons
   */
  activeFab:
    | 'none'
    | 'searchInfras'
    | 'searchUrbanArea'
    | 'searchNearTracks' = 'none';

  layersPanelOpen = false;

  onLayersPanelChange(open: boolean): void {
    this.layersPanelOpen = open;
  }

  //------------------------------------------------------------------------------------------------------------------------------------------------------------

  constructor(
    private ref: ChangeDetectorRef,
    private alertController: AlertController,
    private modalController: ModalController,
    private autopilotService: AutopilotService,
    private router: Router,
    private loadingControl: LoadingController,
    private toastService: ToastService,
    private translateService: TranslateService,
    private httpResponseService: HttpResponsesService,
  ) {}

  /**
   *
   */
  ngOnInit(): void {
    this.isLoaded = false;
    this.loadedTrajectory = null;
    //Obtain trajectory to update or not if is new
    const navState = this.router.getCurrentNavigation()?.extras?.state as
      | { [key: string]: any }
      | undefined;
    const trajectorySelected = navState?.['trajectorySelected'] ?? null;

    if (trajectorySelected !== null) {
      this.isLoaded = true;
      this.loadFlightPlan(trajectorySelected);
    } else {
      this.isLoaded = false;
      this.loadedTrajectory = null;
      this.restartView();
    }

    if (this.user?.profile === 'devel') {
      this.profile = 'devel';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up Leaflet handlers if map exists
    if (this.leafletmap) {
      // Layers are kept for potential reuse
    }
  }

  /**
   * Called from the (mapClick) output of <app-leaflet-map>.
   * This is the reliable entry point for map clicks — avoids registering
   * directly on the child's L.Map instance which can miss events.
   */
  onMapClick(e: L.LeafletMouseEvent): void {
    this.clickMethodAddWaypoint(e.latlng);
  }


  private getLastKey<K, V>(map: Map<K, V>): K | undefined {
    // Map preserves insertion order; iterate keys to find the last without allocating arrays.
    let last: K | undefined = undefined;
    for (const k of map.keys()) {
      last = k;
    }
    return last;
  }

  //#region --> Fab Management

  /**
   * Manage which FAB is active and which one is not.
   * If the user clicks on a FAB, it will be opened.
   * If the user clicks on the same FAB, it will be closed.
   * If the user clicks on another FAB, it will be opened and the previous one will be closed.
   * @param fab
   */
  onFabClick(
    fab:
      | 'searchInfras'
      | 'searchUrbanArea'
      | 'searchNearTracks',
  ) {
    // Click on the same FAB: close it
    this.activeFab = this.activeFab === fab ? 'none' : fab;

    if (fab === 'searchInfras') {
      this.manageSearchNearInfrasDialog();
    }

    if (this.activeFab !== 'searchInfras') {
      this.clearInfrastructures(true);
    }

    if (fab === 'searchUrbanArea') {
      if (this.activeFab === 'searchUrbanArea') {
        // FAB just activated — register the urban areas layer in the panel (under COORDINATION_AREAS)
        this.leafletMapCmp.registerLayer({
          id: 'near-urban-areas-population',
          label: 'Urban Areas Population',
          category: MapLayerCategoryEnum.COORDINATION_AREAS,
          type: 'overlay',
          visible: true,
          order: 16,
          layer: this.urbanAreasLayerGroup,
        });
        this.leafletMapCmp.registerLayer({
          id: 'near-urban-areas-registry',
          label: 'Urban Areas Registry',
          category: MapLayerCategoryEnum.COORDINATION_AREAS,
          type: 'overlay',
          visible: true,
          order: 17,
          layer: this.urbanAreasRegistryLayerGroup,
        });
      }
      this.manageSearchUrbanAreasDialog();
    }

    if (this.activeFab !== 'searchUrbanArea') {
      this.clearUrbanAreas(true);
    }

    if (fab === 'searchNearTracks') {
      this.manageSearchNearTracksDialog();
    }

    if (this.activeFab !== 'searchNearTracks') {
      this.clearNearTracks(true);
    }
  }

  //#endregion Fab Management
  setWaypointFunction(
    command: AutopilotCommand,
    trajectory: AutopilotTrajectory,
    bounds: L.LatLngBounds,
  ) {
    if (command.stageLabel === 'Mission' && this.currentStage !== 'Mission') {
      this.changeStageDirectly('Mission');
    } else if (
      command.stageLabel === 'Arrival' &&
      this.currentStage !== 'Arrival'
    ) {
      this.changeStage('Arrival');
    }

    this.currentWaypointType = AutopilotCommandEnum.SetWaypoint;
    this.trajectoryTree.defaultAltitude = command.point.altitude;
    this.trajectoryTree.defaultSpeed = command.speed;
    this.clickMethodAddWaypoint(
      new L.LatLng(command.point.latitude, command.point.longitude),
    );
    bounds.extend(
      new L.LatLng(command.point.latitude, command.point.longitude),
    );

    if (
      !command?.nextCommandLabel?.includes('JoinRouteFicticious') &&
      command?.nextCommandLabel?.includes('JoinRoute') &&
      this.isFirstFork
    ) {
      this.switchSelectedWaypointType('JoinRoute');
      this.trajectoryTree.defaultAltitude = trajectory.allCommandList.find(
        (cmd) => cmd.label === command.nextCommandLabel,
      ).point.altitude;
      this.trajectoryTree.defaultSpeed = trajectory.allCommandList.find(
        (cmd) => cmd.label === command.nextCommandLabel,
      ).speed;
      this.clickMethodAddWaypoint(
        new L.LatLng(
          trajectory.allCommandList.find(
            (cmd) => cmd.label === command.nextCommandLabel,
          ).point.latitude,
          trajectory.allCommandList.find(
            (cmd) => cmd.label === command.nextCommandLabel,
          ).point.longitude,
        ),
      );
      bounds.extend(
        new L.LatLng(
          trajectory.allCommandList.find(
            (cmd) => cmd.label === command.nextCommandLabel,
          ).point.latitude,
          trajectory.allCommandList.find(
            (cmd) => cmd.label === command.nextCommandLabel,
          ).point.longitude,
        ),
      );

      if (
        !trajectory.allCommandList[
          trajectory.allCommandList.indexOf(command) + 1
        ].label.includes('JoinRoute')
      ) {
        //ADD FORK ENTRY
        let event = {
          apCommand: AutopilotCommandEnum.ForkRoute.valueOf(),
          branchLabel: null,
          label: 'ForkRoute',
          point: this.currentForkLoading.point,
          speed: this.currentForkLoading.speed,
          speedType: AutopilotSpeedTypeEnum.Ground,
          speedUnits: AutopilotSpeedUnitsEnum.ms,
          stageLabel: this.currentStage,
          stateTrajectory: TrajectoryState.InMission.valueOf(),
        };
        this.addForkTrack(event as AutopilotCommand);
        this.currentJoin = trajectory.allCommandList.find(
          (cmd) => cmd.label === command.nextCommandLabel,
        );
      }
    }

    if (
      !trajectory.allCommandList[
        trajectory.allCommandList.indexOf(command) + 1
      ].label.includes('JoinRouteFicticious') &&
      trajectory.allCommandList[
        trajectory.allCommandList.indexOf(command) + 1
      ].label.includes('JoinRoute') &&
      this.currentState === TrajectoryState.CreatingForkTrack.valueOf()
    ) {
      this.closeNewFork();
    }
  }

  setMissionAreaFunction(command: AutopilotCommand, bounds: L.LatLngBounds) {
    if (command.stageLabel === 'Mission' && this.currentStage !== 'Mission') {
      this.changeStageDirectly('Mission');
    } else if (
      command.stageLabel === 'Arrival' &&
      this.currentStage !== 'Arrival'
    ) {
      this.changeStage('Arrival');
    }
    this.currentWaypointType = AutopilotCommandEnum.MissionArea;
    this.currentState = TrajectoryState.CreatingMissionArea;
    this.drawRegionSelected(command.region);
    this.clickMethodAddWaypoint(
      new L.LatLng(command.point.latitude, command.point.longitude),
    );
    bounds.extend(
      new L.LatLng(command.point.latitude, command.point.longitude),
    );
  }

  setForkRouteFunction(command: AutopilotCommand, bounds: L.LatLngBounds) {
    if (command.label !== 'ForkRouteFicticious') {
      if (command.stageLabel === 'Mission' && this.currentStage !== 'Mission') {
        this.changeStageDirectly('Mission');
      } else if (
        command.stageLabel === 'Arrival' &&
        this.currentStage !== 'Arrival'
      ) {
        this.changeStage('Arrival');
      }
      this.switchSelectedWaypointType('ForkRoute');
      this.trajectoryTree.defaultAltitude = command.point.altitude;
      this.trajectoryTree.defaultSpeed = command.speed;
      this.clickMethodAddWaypoint(
        new L.LatLng(command.point.latitude, command.point.longitude),
      );
      this.currentForkLoading = command;
      bounds.extend(
        new L.LatLng(command.point.latitude, command.point.longitude),
      );
    }
  }

  /**
   *
   * @param trajectory
   */
  async loadFlightPlan(trajectory: AutopilotTrajectory) {
    this.loadedTrajectory = trajectory;
    const loading = await this.loadingControl.create({
      message: this.translateService.instant('FP.LOADING.LOADING_FP'),
      spinner: 'bubbles',
      cssClass: 'custom-loading',
    });
    await loading.present();

    this.trajectoryTree.loadFlightPlanData(trajectory);
    this.trajectoryTree.suppressAutoScroll = true;
    const bounds = L.latLngBounds([]);

    trajectory.allCommandList.forEach((command) => {
      switch (command.apCommand) {
        case 'Loiter':
        case 'SetWaypoint': {
          this.setWaypointFunction(command, trajectory, bounds);
          return;
        }
        case 'MissionArea': {
          this.setMissionAreaFunction(command, bounds);
          return;
        }
        case 'Takeoff': {
          this.changeStage('Departure');
          this.addInitialTrack();
          this.trajectoryTree.defaultAltitude = command.point.altitude;
          this.trajectoryTree.defaultSpeed = command.speed;
          this.clickMethodAddWaypoint(
            new L.LatLng(command.point.latitude, command.point.longitude),
          );
          bounds.extend(
            new L.LatLng(command.point.latitude, command.point.longitude),
          );
          return;
        }
        case 'Land': {
          if (this.currentStage !== 'Arrival') {
            this.changeStage('Arrival');
          }
          this.currentWaypointType = AutopilotCommandEnum.Land;
          this.trajectoryTree.defaultAltitude = command.point.altitude;
          this.trajectoryTree.defaultSpeed = command.speed;
          this.clickMethodAddWaypoint(
            new L.LatLng(command.point.latitude, command.point.longitude),
          );
          bounds.extend(
            new L.LatLng(command.point.latitude, command.point.longitude),
          );
          if (
            trajectory.allCommandList.indexOf(command) !==
            trajectory.allCommandList.length - 1
          ) {
            this.addArrivalTrack();
          }
          return;
        }
        case 'ForkRoute': {
          this.setForkRouteFunction(command, bounds);
          return;
        }
      }
    });

    bounds.isValid() && this.leafletmap.fitBounds(bounds);

    this.trajectoryTree.suppressAutoScroll = false;
    loading.dismiss();
  }

  /**
   *
   * @param point
   */
  clickMethodAddWaypoint(point?: L.LatLng) {
    this.currentSelection = null;
    this.attenuateTrajectory();
    if (this.currentState !== TrajectoryState.LandingAdded) {
      let trajectoryDirectory: Directory;
      switch (this.currentStage) {
        case 'Departure': {
          if (!this.trajectoryTree.tree.searchByName(this.currentBranch))
            this.addInitialTrack();

          trajectoryDirectory = this.trajectoryTree.tree.searchByName(
            this.currentBranch,
          ).data;
          break;
        }
        case 'Mission': {
          trajectoryDirectory =
            this.trajectoryTree.tree.searchByName('Mission').data;
          break;
        }
        case 'Arrival': {
          if (!this.trajectoryTree.tree.searchByName(this.currentBranch))
            this.addArrivalTrack();

          trajectoryDirectory = this.trajectoryTree.tree.searchByName(
            this.currentBranch,
          ).data;
          break;
        }
      }

      /* Check if we are in the first fork track and we did and undo
       */
      this.checkForkEntryNeededAfterUndo(trajectoryDirectory);
      /*
       * We apply the waypoint logic to add the new waypoint to the map and the tree. We pass the latitude and longitude of the click.
       */
      this.applyWaypointLogic(point);
    } else {
      this.ref.detectChanges();
    }
  }

  /**
   * Restart everything to the initial state. So restart the trajectory creation and remove anything created.
   */
  restartView() {
    this.changeState(TrajectoryState.CreatingInitialTrack);
    this.changeStage('Departure');
    this.leafletmap?.closePopup();
    this.leafletmap?.flyTo(
      L.latLng(41.27640718208692, 1.9886429501751366),
      12,
      { animate: false, duration: 0.5 },
    ); //center of Catalonia

    this.waypointsDeparture = new Map<string,
      Map<AutopilotCommand, L.Marker>
    >();
    this.waypointsTracksDeparture = new Map<AutopilotCommand, L.Polyline>();

    this.waypointsMission = new Map<AutopilotCommand, L.Marker>();
    this.waypointsTracksMission = new Map<AutopilotCommand, L.Polyline>();

    this.waypointsArrival = new Map<string, Map<AutopilotCommand, L.Marker>>();
    this.waypointsTracksArrival = new Map<AutopilotCommand, L.Polyline>();

    this.tracksBetweenStages = new Map<string, L.Polyline>();

    this.markersLayer.clearLayers();
    this.tracksLayer.clearLayers();
    this.polygonLayers.clearLayers();

    this.trajectoryTree?.initializeTrajectoryTree();
  }

  /**
   *
   */
  saveAutopilotTrajectory() {
    if (
      this.trajectoryTree.trajectoryName &&
      this.trajectoryTree.trajectoryVersion &&
      this.trajectoryTree.trajectorySearchKeys &&
      this.trajectoryTree.trajectoryPath
    ) {
      let trajectory: AutopilotTrajectory = {
        name: this.trajectoryTree.trajectoryName,
        version: this.trajectoryTree.trajectoryVersion,
        uniqueId: '',
        searchKeys: this.trajectoryTree.trajectorySearchKeys,
        path: this.trajectoryTree.trajectoryPath,
        date: new Date(),
        centroid: this.trajectoryUtils.obtainTrajectoryCentroid(
          this.trajectoryTree.tree,
        ),
        organizationName: this.user?.organizationName,
        organizationUuid: this.user?.organizationUuid,
        description: this.trajectoryTree.trajectoryDescription,
        type: AutopilotTrajectoryTypeEnum.Mission,
        detail: AutopilotTrajectoryDetailEnum.Preliminary,
        status: AutopilotTrajectoryStatusEnum.Initial,
        allCommandList: this.trajectoryUtils.createCommandListFromTree(
          this.trajectoryTree.tree,
        ),
        selectionList: [],
        labelMap: new Map<string, AutopilotCommand>(),
        initialLabelList: [],
        typeKey: toInteger(GenericObjectTypeEnum.OBJECT_AUTOPILOT_TRAJECTORY),
        ownerUuid: this.user?.organizationUuid,
        time: null,
        location: null,
        sourceReference: null
      };
      console.log(trajectory);
      this.toastService.presentToast(
        this.translateService.instant('FP.TOAST.SAVING_FLIGHT_PLAN'),
        'bottom',
        5000,
        '',
        'success',
      );
    } else {
      this.presentErrorAddingTrajectory(
        this.translateService.instant('FP.TOAST.FILL_ATTRIBUTES'),
      );
    }
  }

  async updateTrajectory() {
    if (
      this.trajectoryTree.trajectoryName &&
      this.trajectoryTree.trajectoryVersion &&
      this.trajectoryTree.trajectorySearchKeys &&
      this.trajectoryTree.trajectoryPath
    ) {
      let commands: Array<AutopilotCommand> =
        this.trajectoryUtils.createCommandListFromTree(
          this.trajectoryTree.tree,
        );

      if (commands.length === 0) {
        this.presentErrorAddingTrajectory(
          this.translateService.instant('FP.TOAST.THREE_WAYPOINTS'),
        );
        return;
      }

      if (
        commands.filter((command) => command.label.includes('Land')).length ===
        0
      ) {
        this.presentErrorAddingTrajectory(
          this.translateService.instant('FP.TOAST.NO_LANDING'),
        );
        return;
      }

      const loading = await this.loadingControl.create({
        message: this.translateService.instant('FP.LOADING.UPDATING_FP'),
        spinner: 'bubbles',
        cssClass: 'custom-loading',
      });
      await loading.present();

      this.loadedTrajectory.allCommandList = commands;
      this.loadedTrajectory.centroid =
        this.trajectoryUtils.obtainTrajectoryCentroid(this.trajectoryTree.tree);
      this.loadedTrajectory.name = this.trajectoryTree.trajectoryName;
      this.loadedTrajectory.version = this.trajectoryTree.trajectoryVersion;
      this.loadedTrajectory.searchKeys =
        this.trajectoryTree.trajectorySearchKeys;
      this.loadedTrajectory.path = this.trajectoryTree.trajectoryPath;
      this.loadedTrajectory.description =
        this.trajectoryTree.trajectoryDescription;

      await firstValueFrom(
        this.autopilotService.updateAutopilotTrajectory(
          this.user.organizationUuid,
          this.trajectoryUtils.applyAutopilotTrajectoryLogic(
            this.loadedTrajectory,
          ),
        ),
      )
        .then((res: HttpResponse<MultipleApiResponse>) => {
          this.restartView();
          loading.dismiss();
          this.router.navigate(['/new-trajectories-page']);
        })
        .catch((resError: HttpErrorResponse) => {
          loading.dismiss();
          this.httpResponseService.showServerError(
            resError?.status,
            resError?.error,
          );
        });
    } else {
      this.httpResponseService.showServerError(
        400,
        this.translateService.instant('FP.TOAST.FILL_ATTRIBUTES_CREATE'),
      );
    }
  }

  /**
   *
   */
  async createAutopilotTrajectory() {
    if (
      this.trajectoryTree.trajectoryName &&
      this.trajectoryTree.trajectoryVersion &&
      this.trajectoryTree.trajectorySearchKeys &&
      this.trajectoryTree.trajectoryPath
    ) {
      let commands: Array<AutopilotCommand> =
        this.trajectoryUtils.createCommandListFromTree(
          this.trajectoryTree.tree,
        );

      if (commands.length === 0) {
        this.presentErrorAddingTrajectory(
          this.translateService.instant('FP.TOAST.THREE_WAYPOINTS'),
        );
        return;
      }

      if (
        commands.filter((command) => command.label.includes('Land')).length ===
        0
      ) {
        this.presentErrorAddingTrajectory(
          this.translateService.instant('FP.TOAST.NO_LANDING'),
        );
        return;
      }

      const loading = await this.loadingControl.create({
        message: this.translateService.instant('FP.LOADING.CREATING_FP'),
        spinner: 'bubbles',
        cssClass: 'custom-loading',
      });
      await loading.present();
      let trajectory: AutopilotTrajectory = {
        version: this.trajectoryTree.trajectoryVersion,
        searchKeys: this.trajectoryTree.trajectorySearchKeys,
        path: this.trajectoryTree.trajectoryPath,
        date: new Date(),
        centroid: this.trajectoryUtils.obtainTrajectoryCentroid(
          this.trajectoryTree.tree,
        ),
        organizationName: this.user?.organizationName,
        organizationUuid: this.user?.organizationUuid,
        description: this.trajectoryTree.trajectoryDescription,
        type: AutopilotTrajectoryTypeEnum.Mission,
        detail: AutopilotTrajectoryDetailEnum.AutoDetail,
        status: AutopilotTrajectoryStatusEnum.Execution,
        allCommandList: commands,
        selectionList: [],
        labelMap: new Map<string, AutopilotCommand>(),
        initialLabelList: [],
        typeKey: toInteger(GenericObjectTypeEnum.OBJECT_AUTOPILOT_TRAJECTORY),
        ownerUuid: this.user?.organizationUuid,
        time: null,
        location: null,
        sourceReference: null,
        name: this.trajectoryTree.trajectoryName,
        uniqueId: null,
      };

      await firstValueFrom(
        this.autopilotService.createAutopilotTrajectory(
          this.trajectoryUtils.applyAutopilotTrajectoryLogic(trajectory),
        ),
      )
        .then((res: HttpResponse<MultipleApiResponse>) => {
          this.restartView();
          loading.dismiss();
          this.router.navigate(['/new-trajectories-page']);
        })
        .catch((resError: HttpErrorResponse) => {
          loading.dismiss();
          this.httpResponseService.showServerError(
            resError?.status,
            resError?.error,
          );
        });
    } else {
      this.httpResponseService.showServerError(
        400,
        this.translateService.instant('FP.TOAST.FILL_ATTRIBUTES_CREATE'),
      );
    }
  }

  /**
   * Error adding trajectory
   */
  async presentErrorAddingTrajectory(res: string) {
    const alert = await this.alertController.create({
      header: this.translateService.instant('FP.TOAST.CREATING'),
      message: res,
      cssClass: 'custom-alert',
      buttons: [
        {
          text: this.translateService.instant('COMMON.BUTTONS.OK'),
          role: 'cancel',
          handler: () => null,
        },
      ],
    });

    await alert.present();
  }

  /**
   * Force an update of the tree after anything has changed (removing, adding or editing of waypoints or tracks)
   */
  updateTree() {
    this.refreshStageJoints();
    this.trajectoryTree.updateTree();
  }

  /**
   * This method adds and refresh the dotted lines between stages. It checks if there are waypoints in the departure and arrival stages and link
   * them with the mission stage.
   */
  refreshStageJoints() {
    this.tracksBetweenStages.forEach((value: L.Polyline, key: string) => {
      this.tracksLayer.removeLayer(value);
    });
    this.tracksBetweenStages = new Map<string, L.Polyline>();

    let firstWaypointFromMission = Array.from(this.waypointsMission.keys())[0];
    if (this.waypointsDeparture.size > 0 && firstWaypointFromMission) {
      this.waypointsDeparture.forEach(
        (value: Map<AutopilotCommand, L.Marker>, key: string) => {
          let lastWaypointFromDeparture = this.getLastKey(value);
          if (lastWaypointFromDeparture) {
            let track = L.polyline(
              [
                new L.LatLng(
                  lastWaypointFromDeparture?.point?.latitude,
                  lastWaypointFromDeparture?.point?.longitude,
                ),
                new L.LatLng(
                  firstWaypointFromMission?.point?.latitude,
                  firstWaypointFromMission?.point?.longitude,
                ),
              ],
              {
                color: this.colors.getColor('Departure'),
                weight: 2,
                opacity: 0.5,
                dashArray: '7, 5',
                dashOffset: '20',
              },
            ).arrowheads({ size: '12px', frequency: 2 });
            if (track) {
              this.tracksLayer.addLayer(track);
              this.tracksBetweenStages.set(key, track);
            }
          }
        },
      );
    }
    let lastWaypointFromMission = Array.from(
      this.waypointsMission.keys(),
    ).pop();
    if (this.waypointsArrival.size > 0 && lastWaypointFromMission) {
      this.waypointsArrival.forEach(
        (value: Map<AutopilotCommand, L.Marker>, key: string) => {
          let firstWaypointFromArrival = Array.from(value.keys())[0];
          if (firstWaypointFromArrival) {
            let track = L.polyline(
              [
                new L.LatLng(
                  lastWaypointFromMission?.point?.latitude,
                  lastWaypointFromMission?.point?.longitude,
                ),
                new L.LatLng(
                  firstWaypointFromArrival?.point?.latitude,
                  firstWaypointFromArrival?.point?.longitude,
                ),
              ],
              {
                color: this.colors.getColor('Arrival'),
                weight: 2,
                opacity: 0.5,
                dashArray: '7, 5',
                dashOffset: '20',
              },
            ).arrowheads({ size: '12px', frequency: 2 });
            if (track) {
              this.tracksLayer.addLayer(track);
              this.tracksBetweenStages.set(key, track);
            }
          }
        },
      );
    }
  }

  /**
   * Remove All trajectory
   */
  async presentClearEverythingConfirmation() {
    const clearTrajectory = () => {
      this.restartView();
    };

    const alert = await this.alertController.create({
      header: this.translateService.instant('FP.TOAST.CONFIRM_TITLE'),
      message: this.translateService.instant('FP.TOAST.CONFIRM_CLEAR_MESSAGE'),
      cssClass: 'custom-alert',
      buttons: [
        {
          text: this.translateService.instant('COMMON.BUTTONS.CANCEL'),
          role: 'cancel',
          handler: () => null,
        },
        {
          text: this.translateService.instant('COMMON.BUTTONS.SURE'),
          handler: () => {
            clearTrajectory();
          },
        },
      ],
    });

    await alert.present();
  }

  /**  If we were adding the first fork track but we did an undo to the Fork Route we need to add again the Fork Entry.
   * This is because the undo removes the Fork Entry from the tree but not the Fork Track from the map. So we are still in a Fork
   * but the entry from the tree has been removed. SPECIAL CASE
   */
  checkForkEntryNeededAfterUndo(trajectoryDirectory: Directory) {
    try {
      if (
        !trajectoryDirectory.items.find((item) => item?.name?.includes('Fork'))
      ) {
        switch (this.currentStage) {
          case 'Departure': {
            const depBranchFork = this.waypointsDeparture.get(this.currentBranch);
            if (
              depBranchFork &&
              Array.from(depBranchFork.keys()).pop() &&
              Array.from(depBranchFork.keys()).pop().apCommand === 'ForkRoute'
            ) {
              this.trajectoryTree.startForkEntry(
                this.currentStage,
                this.currentState,
                null,
                this.currentBranch,
              );
              this.changeState(TrajectoryState.InForkTrack);
            }
            break;
          }
          case 'Mission': {
            if (
              this.getLastKey(this.waypointsMission) &&
              this.getLastKey(this.waypointsMission).apCommand ===
                'ForkRoute'
            ) {
              this.trajectoryTree.startForkEntry(
                this.currentStage,
                this.currentState,
                null,
                this.currentBranch,
              );
              this.changeState(TrajectoryState.InForkTrack);
            }
            break;
          }
          case 'Arrival': {
            const arrBranchFork = this.waypointsArrival.get(this.currentBranch);
            if (
              arrBranchFork &&
              Array.from(arrBranchFork.keys()).pop() &&
              Array.from(arrBranchFork.keys()).pop().apCommand === 'ForkRoute'
            ) {
              this.trajectoryTree.startForkEntry(
                this.currentStage,
                this.currentState,
                null,
                this.currentBranch,
              );
              this.changeState(TrajectoryState.InForkTrack);
            }
            break;
          }
        }
      }
    } catch (error) {
      switch (this.currentStage) {
        case 'Departure': {
          this.toastService.presentToast(
            this.translateService.instant('FP.TOAST.SELECT_INITIAL'),
            'bottom',
            5000,
            '',
            'warning',
          );
          break;
        }
        case 'Mission': {
          break;
        }
        case 'Arrival': {
          this.toastService.presentToast(
            this.translateService.instant('FP.TOAST.SELECT_INITIAL'),
            'bottom',
            5000,
            '',
            'warning',
          );
          break;
        }
      }
    }
  }

  /**
   * This method is called from the leaflet map click event, but also when the user clicks on the tree to add a new initial track.
   * It enters the CreatingInitialTrack state, adds the entry to the tree and refresh the joints between stages.
   */
  addInitialTrack() {
    this.currentSelection = null;
    this.attenuateTrajectory();
    if (
      !this.isFirstFork &&
      !this.isAddingFork &&
      this.currentState !== TrajectoryState.CreatingForkTrack &&
      this.currentState !== TrajectoryState.InForkTrack &&
      this.currentState !== TrajectoryState.CreatingMissionArea &&
      this.currentState !== TrajectoryState.ClosingMissionArea &&
      this.currentState !== TrajectoryState.CreatingMissionAreaInFork &&
      this.currentState !== TrajectoryState.ClosingMissionAreaInFork
    ) {
      this.changeState(TrajectoryState.CreatingInitialTrack);
      this.changeStage('Departure');
      this.trajectoryTree.startInitialEntry();
      this.refreshStageJoints();
    } else {
      this.toastService.presentToast(
        this.translateService.instant('FP.TOAST.FINISH_FORK_INITIAL'),
        'bottom',
        5000,
        '',
        'warning',
      );
    }
    this.ref.detectChanges();
  }

  /**
   * This method is called from the child component @trajectoryTree once the entry has been added to the tree.
   * @param trackName: Name of the new initial track that has been used in the tree.
   */
  initialTrackCreated(trackName: string) {
    this.waypointsDeparture.set(
      trackName,
      new Map<AutopilotCommand, L.Marker>(),
    );
    this.currentBranch = trackName;
  }

  /**
   * This method is called from the leaflet map click event, but also when the user clicks on the tree to add a new arrival track.
   * It enters the CreatingArrivalTrack state, adds the entry to the tree and refresh the joints between stages.
   */
  addArrivalTrack() {
    this.currentSelection = null;
    this.attenuateTrajectory();
    if (
      !this.isFirstFork &&
      !this.isAddingFork &&
      this.currentState !== TrajectoryState.CreatingForkTrack &&
      this.currentState !== TrajectoryState.InForkTrack &&
      this.currentState !== TrajectoryState.CreatingMissionArea &&
      this.currentState !== TrajectoryState.ClosingMissionArea &&
      this.currentState !== TrajectoryState.CreatingMissionAreaInFork &&
      this.currentState !== TrajectoryState.ClosingMissionAreaInFork
    ) {
      this.changeState(TrajectoryState.CreatingArrivalTrack);
      this.changeStage('Arrival');
      this.trajectoryTree.startArrivalEntry();
      this.refreshStageJoints();
    } else {
      this.toastService.presentToast(
        this.translateService.instant('FP.TOAST.FINISH_FORK_ARRIVAL'),
        'bottom',
        5000,
        '',
        'warning',
      );
    }
    this.ref.detectChanges();
  }

  /**
   * This method is called from the child component @trajectoryTree once the entry has been added to the tree.
   * @param trackName: Name of the new arrival track that has been used in the tree.
   */
  arrivalTrackCreated(trackName: string) {
    this.waypointsArrival.set(trackName, new Map<AutopilotCommand, L.Marker>());
    this.currentBranch = trackName;
  }

  /**
   * This method adds a new Fork to the tree (called from child component) and prepare all the variables to add the waypoints to the fork.
   * First it saves the last waypoint added to the stage, then it activates the isAddingFork boolean to control the state of the trajectory creation.
   * After that it stores the JoinRoute and the ForkRoute waypoints associated to the new Fork and enters the state CreatingForkTrack.
   * @param event: last waypoint added to the stage, it is needed to keep the order after adding the new fork.
   */
  addForkTrack(event: AutopilotCommand) {
    this.currentSelection = null;
    this.attenuateTrajectory();
    if (
      !this.isFirstFork &&
      !this.isAddingFork &&
      this.currentState !== TrajectoryState.CreatingForkTrack &&
      this.currentState !== TrajectoryState.InForkTrack &&
      this.currentState !== TrajectoryState.CreatingMissionArea &&
      this.currentState !== TrajectoryState.ClosingMissionArea &&
      this.currentState !== TrajectoryState.CreatingMissionAreaInFork &&
      this.currentState !== TrajectoryState.ClosingMissionAreaInFork
    ) {
      let waypoint;
      switch (this.currentStage) {
        case 'Departure': {
          waypoint = Array.from(
            this.waypointsDeparture.get(this.currentBranch).keys(),
          ).pop();
          break;
        }
        case 'Mission': {
          waypoint = this.getLastKey(this.waypointsMission);
          break;
        }
        case 'Arrival': {
          waypoint = Array.from(
            this.waypointsArrival.get(this.currentBranch).keys(),
          ).pop();
          break;
        }
      }

      this.lastWaypoint = waypoint;
      this.isAddingFork = true;
      this.currentJoin = this.trajectoryTree.startForkEntry(
        event.stageLabel,
        event.stateTrajectory,
        event,
        this.currentBranch,
      );
      this.currentFork = event;
      this.changeState(TrajectoryState.CreatingForkTrack);
      this.changeStage(event.stageLabel);
      this.refreshStageJoints();
    } else {
      this.toastService.presentToast(
        this.translateService.instant('FP.TOAST.FINISH_FORK_NEW'),
        'bottom',
        5000,
        '',
        'warning',
      );
    }
    this.ref.detectChanges();
  }

  /**
   * Method called from the button in the interface to close the fork track creation and add the last track to the JoinRoute Waypoint.
   * Depending on the stage it will close the frok and change the state to InInitialTrack, InMission or InArrivalTrack. Finally it will reset
   * the lastWaypoint and isAddingFork variables.
   */
  closeNewFork() {
    switch (this.currentStage) {
      case 'Departure': {
        if (
          !this.currentFork ||
          this.trajectoryTree.tree
            .searchByName(this.currentBranch)
            .data.items.find((item) => item.name === this.currentFork.forkLabel)
            .items
        ) {
          this.closeForkTrackPerStage(
            this.waypointsDeparture.get(this.currentBranch),
            this.waypointsTracksDeparture,
          );
          this.changeState(TrajectoryState.InInitialTrack);
          this.isAddingFork = false;
          this.ref.detectChanges();
        } else {
          this.toastService.presentToast(
            this.translateService.instant('FP.TOAST.ADD_WAYPOINT'),
            'bottom',
            5000,
            '',
            'warning',
          );
        }
        break;
      }
      case 'Mission': {
        if (
          !this.currentFork ||
          this.trajectoryTree.tree
            .searchByName(this.currentStage)
            .data.items.find((item) => item.name === this.currentFork.forkLabel)
            .items
        ) {
          this.closeForkTrackPerStage(
            this.waypointsMission,
            this.waypointsTracksMission,
          );
          this.changeState(TrajectoryState.InMission);
          this.isAddingFork = false;
          this.ref.detectChanges();
        } else {
          this.toastService.presentToast(
            this.translateService.instant('FP.TOAST.ADD_WAYPOINT'),
            'bottom',
            5000,
            '',
            'warning',
          );
        }
        break;
      }
      case 'Arrival': {
        if (
          !this.currentFork ||
          this.trajectoryTree.tree
            .searchByName(this.currentBranch)
            .data.items.find((item) => item.name === this.currentFork.forkLabel)
            .items
        ) {
          this.closeForkTrackPerStage(
            this.waypointsArrival.get(this.currentBranch),
            this.waypointsTracksArrival,
          );
          this.changeState(TrajectoryState.InArrivalTrack);
          this.isAddingFork = false;
          this.ref.detectChanges();
        } else {
          this.toastService.presentToast(
            this.translateService.instant('FP.TOAST.ADD_WAYPOINT'),
            'bottom',
            5000,
            '',
            'warning',
          );
        }
        break;
      }
    }
  }

  /**
   * This method will add the last track of a fork, so the track that ends in the JoinRoute Waypoint, and it will add an extra waypoint with
   * specific information of which fork it belongs only for the undo logic and the remove selection logic.
   */
  closeForkTrackPerStage(
    waypointVector: Map<AutopilotCommand, L.Marker>,
    trackVector: Map<AutopilotCommand, L.Polyline>,
  ) {
    let newWaypoint = this.trajectoryUtils.obtainAPCommand(
      this.currentJoin.point.latitude,
      this.currentJoin.point.longitude,
      this.getAltitude(),
      this.getSpeed(),
      this.currentJoin.apCommand,
      this.currentState,
      this.currentStage,
      this.currentWaypointType,
      this.currentBranch,
      null,
    );
    newWaypoint.label = 'LastWaypoint';
    newWaypoint.apCommand = this.currentJoin.apCommand;
    newWaypoint.forkLabel = 'Fork ' + (this.trajectoryTree.forkCounter - 1);

    const marker = L.marker(new L.LatLng(0, 0), {
      icon: this.icons.getIcon(
        this.currentStage + '-' + this.currentWaypointType,
      ),
    });

    let latitude = (this.getLastKey(waypointVector) as AutopilotCommand)
      .point.latitude;
    let longitude = (
      this.getLastKey(waypointVector) as AutopilotCommand
    ).point.longitude;
    const track = L.polyline([
      new L.LatLng(latitude, longitude),
      new L.LatLng(
        this.currentJoin.point.latitude,
        this.currentJoin.point.longitude,
      ),
    ]).arrowheads({ size: '12px', frequency: 2 });

    track.setStyle({
      color: this.colors.getColor(this.currentStage + '-Fork'),
      weight: 4,
      opacity: 0.8,
    });
    track.on('click', (e: L.LeafletMouseEvent) => {
      L.DomEvent.stopPropagation(e);
      this.addMiddleWaypoint(newWaypoint, e.latlng);
    });
    this.tracksLayer.addLayer(track);
    trackVector.set(newWaypoint, track);

    if (
      this.lastWaypoint.point.latitude === this.currentJoin.point.latitude &&
      this.lastWaypoint.point.longitude === this.currentJoin.point.longitude
    ) {
      waypointVector.set(newWaypoint, marker);
    } else {
      waypointVector.set(newWaypoint, marker);
      let extraWaypoint = this.trajectoryUtils.obtainAPCommand(
        this.lastWaypoint.point.latitude,
        this.lastWaypoint.point.longitude,
        this.getAltitude(),
        this.getSpeed(),
        this.lastWaypoint.apCommand,
        this.currentState,
        this.currentStage,
        this.currentWaypointType,
        this.currentBranch,
        null,
      );
      extraWaypoint.label = 'LastWaypoint';
      extraWaypoint.apCommand = this.lastWaypoint.apCommand;

      waypointVector.set(extraWaypoint, marker);
    }

    this.currentJoin = null;
    this.refreshStageJoints();
  }

  /**
   * Method called from the button in the interface to remove the branch/fork selected from the tree. It will remove all the waypoints and tracks
   * associated to the branch/fork and also the extra waypoints added for the undo logic and the remove selection logic.
   */
  removeCurrentSelection(waypoint: AutopilotCommand) {
    /*
     * This first check is to know whether the selection we have is an initial, arrival or mission branch or if is a specific fork track.
     */
    if (
      this.currentSelection &&
      this.trajectoryTree.tree.searchByName(this.currentSelection)
    ) {
      // The current selection is an initial, arrival or mission branch. So from the tree we remove all the waypoints related to it.
      this.trajectoryTree.tree
        .searchByName(this.currentSelection)
        .data.items.forEach((item: PlanningItem) => {
          if (item.name && item.name.includes('Fork')) {
            item.items.forEach((subItem: AutopilotCommand) => {
              if (subItem.stageLabel === 'Departure') {
                this.removeWaypoint(
                  subItem,
                  this.waypointsDeparture.get(item.parentRef.name),
                  this.waypointsTracksDeparture,
                  this.regionsDeparture,
                  true,
                );
              } else if (subItem.stageLabel === 'Mission') {
                this.removeWaypoint(
                  subItem,
                  this.waypointsMission,
                  this.waypointsTracksMission,
                  this.regionsMission,
                  true,
                );
              } else if (subItem.stageLabel === 'Arrival') {
                this.removeWaypoint(
                  subItem,
                  this.waypointsArrival.get(item.parentRef.name),
                  this.waypointsTracksArrival,
                  this.regionsArrival,
                  true,
                );
              }
            });
          } else {
            if (item.stageLabel === 'Departure') {
              this.removeWaypoint(
                item,
                this.waypointsDeparture.get(this.currentSelection),
                this.waypointsTracksDeparture,
                this.regionsDeparture,
                true,
              );
            } else if (item.stageLabel === 'Mission') {
              this.removeWaypoint(
                item,
                this.waypointsMission,
                this.waypointsTracksMission,
                this.regionsMission,
                true,
              );
            } else if (item.stageLabel === 'Arrival') {
              this.removeWaypoint(
                item,
                this.waypointsArrival.get(this.currentSelection),
                this.waypointsTracksArrival,
                this.regionsArrival,
                true,
              );
            }
          }
        });

      // After we removed the waypoints related to the tree we remove the extra waypoints added to the vectors for main logic purposes.
      // We do it separetely depending if we are in an initial, arrival or mission branch.

      // Initial branch extra waypoints removal and tree branch removal.
      if (this.currentSelection.includes('Track Initial')) {
        const depBranchSnapshot = Array.from(
          this.waypointsDeparture.get(this.currentSelection).keys(),
        );
        depBranchSnapshot.forEach((key: AutopilotCommand) => {
          this.removeWaypoint(
            key,
            this.waypointsDeparture.get(this.currentSelection),
            this.waypointsTracksDeparture,
            this.regionsDeparture,
            true,
          );
        });
        this.waypointsDeparture.delete(this.currentSelection);
        this.updateTree();
        if (this.currentBranch === this.currentSelection) {
          this.currentBranch = null;
          this.trajectoryTree.tree
            .obtainArray()
            .forEach((item: PlanningItem) => {
              if (item.name && item.name.includes('Track Initial')) {
                if (!this.currentBranch) this.currentBranch = item.name;
              }
            });
        }
        this.trajectoryTree.tree.delete(
          this.trajectoryTree.tree.searchByName(this.currentSelection).data,
        );
        if (this.waypointsDeparture.size > 0) {
          this.changeState(TrajectoryState.InInitialTrack);
        } else {
          this.changeState(TrajectoryState.CreatingInitialTrack);
        }
      }

      // Arrival branch extra waypoints removal and tree branch removal.
      if (this.currentSelection.includes('Track Arrival')) {
        const arrBranchSnapshot = Array.from(
          this.waypointsArrival.get(this.currentSelection).keys(),
        );
        arrBranchSnapshot.forEach((key: AutopilotCommand) => {
          this.removeWaypoint(
            key,
            this.waypointsArrival.get(this.currentSelection),
            this.waypointsTracksArrival,
            this.regionsArrival,
            true,
          );
        });
        this.waypointsArrival.delete(this.currentSelection);
        this.updateTree();
        if (this.currentBranch === this.currentSelection) {
          this.currentBranch = null;
          this.trajectoryTree.tree
            .obtainArray()
            .forEach((item: PlanningItem) => {
              if (item.name && item.name.includes('Track Arrival')) {
                if (!this.currentBranch) this.currentBranch = item.name;
              }
            });
        }
        this.trajectoryTree.tree.delete(
          this.trajectoryTree.tree.searchByName(this.currentSelection).data,
        );
        if (this.waypointsArrival.size > 0) {
          this.changeState(TrajectoryState.InArrivalTrack);
        } else {
          this.changeStageDirectly('Mission');
        }
      }

      // Mission branch extra waypoints removal and tree branch removal.
      if (this.currentSelection.includes('Mission')) {
        const missionSnapshot = Array.from(this.waypointsMission.keys());
        missionSnapshot.forEach((key: AutopilotCommand) => {
          this.removeWaypoint(
            key,
            this.waypointsMission,
            this.waypointsTracksMission,
            this.regionsMission,
            true,
          );
        });
        this.trajectoryTree.tree.searchByName(
          this.currentSelection,
        ).data.items = [];
      }

      this.updateTree();
      this.currentSelection = null;
      this.ref.detectChanges();
    } else {
      // The current selection is a specific fork track.

      // First we check if the fork selected is the last remaining or not.
      let lastFork = true;

      let stage = waypoint ? waypoint.stageLabel : this.currentStage;
      let branch = waypoint
        ? waypoint.stageLabel === 'Mission'
          ? 'Mission'
          : waypoint.branchLabel
        : stage === 'Mission'
          ? 'Mission'
          : this.currentBranch;

      this.trajectoryTree.tree
        .searchByName(branch)
        ?.data.items.forEach((item: PlanningItem) => {
          if (
            item.name &&
            item.name.includes('Fork') &&
            item.name !== this.currentSelection
          ) {
            lastFork = false;
          }
        });

      // If the fork selected is not the last we will remove it from the trajectory.

      // We remove all the waypoints associated to the fork from teh vectors.
      this.trajectoryTree.tree
        .searchByName(branch)
        ?.data.items.forEach((item: PlanningItem) => {
          if (item.name && item.name === this.currentSelection) {
            item.items.forEach((subItem: AutopilotCommand) => {
              if (subItem.stageLabel === 'Departure') {
                this.removeWaypoint(
                  subItem,
                  this.waypointsDeparture.get(item.parentRef.name),
                  this.waypointsTracksDeparture,
                  this.regionsDeparture,
                  false,
                );
              } else if (subItem.stageLabel === 'Mission') {
                this.removeWaypoint(
                  subItem,
                  this.waypointsMission,
                  this.waypointsTracksMission,
                  this.regionsMission,
                  false,
                );
              } else if (subItem.stageLabel === 'Arrival') {
                this.removeWaypoint(
                  subItem,
                  this.waypointsArrival.get(item.parentRef.name),
                  this.waypointsTracksArrival,
                  this.regionsArrival,
                  false,
                );
              }
            });
          }
        });

      // After cleaning the vectors we remove the fork from the tree.
      let itemRemove;
      this.trajectoryTree.tree
        .searchByName(branch)
        ?.data.items.forEach((item: PlanningItem) => {
          if (item.name && item.name === this.currentSelection) {
            itemRemove = item;
          }
        });
      let fork;
      if (itemRemove) {
        fork =
          this.trajectoryTree.tree.searchByName(branch)?.data.items[
            this.trajectoryTree.tree
              .searchByName(branch)
              ?.data.items.indexOf(itemRemove) - 1
          ];
        this.trajectoryTree.tree
          .searchByName(branch)
          ?.data.items.splice(
            this.trajectoryTree.tree
              .searchByName(branch)
              ?.data.items.indexOf(itemRemove),
            1,
          );
        this.updateTree();
      }

      // Finally depending on the stage we will remove the extra waypoints added for the undo and remove logic purposes.
      switch (stage) {
        case 'Departure': {
          this.waypointsDeparture
            .get(branch)
            .forEach((value: L.Marker, key: AutopilotCommand) => {
              if (key.forkLabel === this.currentSelection) {
                this.removeWaypoint(
                  key,
                  this.waypointsDeparture.get(branch),
                  this.waypointsTracksDeparture,
                  this.regionsDeparture,
                  false,
                );
              }
            });
          break;
        }
        case 'Mission': {
          this.waypointsMission.forEach(
            (value: L.Marker, key: AutopilotCommand) => {
              if (key.forkLabel === this.currentSelection) {
                this.removeWaypoint(
                  key,
                  this.waypointsMission,
                  this.waypointsTracksMission,
                  this.regionsMission,
                  false,
                );
              }
            },
          );
          break;
        }
        case 'Arrival': {
          this.waypointsArrival
            .get(branch)
            .forEach((value: L.Marker, key: AutopilotCommand) => {
              if (key.forkLabel === this.currentSelection) {
                this.removeWaypoint(
                  key,
                  this.waypointsArrival.get(branch),
                  this.waypointsTracksArrival,
                  this.regionsArrival,
                  false,
                );
              }
            });
          break;
        }
      }

      this.currentSelection = null;
      this.ref.detectChanges();

      if (lastFork && fork) {
        this.addForkTrack(fork);
        this.toastService.presentToast(
          this.translateService.instant('FP.TOAST.ADD_NEW_FORK'),
          'bottom',
          5000,
          '',
          'warning',
        );
      }
    }
  }

  /**
   * Method called from the child component @trajectoryTree by clicking on a branch/fork. It will highlight the branch/fork clicked and change the state
   * and stage to the one selected to modify it. It will also make appear the button to remove the selection from the trajectory.
   * @param treeNode: branch/fork selected from the tree to be highlighted and prepared for removal.
   */
  selectTrack(treeNode: Directory) {
    // We don't enable this feature if we are adding a fork (first or not).
    if (
      !this.isFirstFork &&
      !this.isAddingFork &&
      this.currentState !== TrajectoryState.CreatingForkTrack &&
      this.currentState !== TrajectoryState.InForkTrack &&
      this.currentState !== TrajectoryState.CreatingMissionArea &&
      this.currentState !== TrajectoryState.ClosingMissionArea &&
      this.currentState !== TrajectoryState.CreatingMissionAreaInFork &&
      this.currentState !== TrajectoryState.ClosingMissionAreaInFork
    ) {
      this.currentSelection = treeNode.name;
      this.attenuateTrajectory();

      // After deselecting anything we will highlight the selected branch/fork. To do it we check if we have selected a fork or an initial, arrival
      // or mission branch.
      if (treeNode.name.includes('Fork')) {
        treeNode.items.forEach((item: PlanningItem) => {
          this.changeStage(item.stageLabel);
          this.highlightTrajectorySelection(
            treeNode.parentRef.name,
            item,
            true,
          );
        });
        this.currentBranch = treeNode.parentRef.name;
      } else {
        treeNode.items.forEach((item: PlanningItem) => {
          if (item.name && item.name.includes('Fork')) {
            item.items.forEach((subItem: AutopilotCommand) => {
              this.highlightTrajectorySelection(
                this.currentSelection,
                subItem,
                true,
              );
            });
          } else {
            this.highlightTrajectorySelection(
              this.currentSelection,
              item,
              false,
            );
          }
          this.changeStage(item.stageLabel);
          if (item.apCommand === 'Land') {
            this.changeState(TrajectoryState.LandingAdded);
          }
        });
        this.currentBranch = this.currentSelection;
      }
      switch (this.currentStage) {
        case 'Departure': {
          this.changeState(TrajectoryState.InInitialTrack);
          break;
        }
        case 'Mission': {
          this.changeState(TrajectoryState.InMission);
          break;
        }
        case 'Arrival': {
          if (this.currentState !== TrajectoryState.LandingAdded)
            this.changeState(TrajectoryState.InArrivalTrack);
          break;
        }
      }
      this.ref.detectChanges();
    } else {
      this.toastService.presentToast(
        this.translateService.instant('FP.TOAST.CANNOT_SELECT_DURING_FORK'),
        'bottom',
        5000,
        '',
        'warning',
      );
    }
  }

  /**
   * Method to return the highlighted waypoints and tracks to normal.
   */
  attenuateTrajectory() {
    this.waypointsDeparture.forEach(
      (waypointVector: Map<AutopilotCommand, L.Marker>, branchName: string) => {
        waypointVector.forEach((value: L.Marker, key: AutopilotCommand) => {
          value.setIcon(this.icons.getIcon('Departure-' + key.apCommand));
          if (this.waypointsTracksDeparture.get(key)) {
            if (key.forkLabel) {
              if (key.stateTrajectory === TrajectoryState.ClosingMissionArea) {
                this.waypointsTracksDeparture
                  .get(key)
                  .setStyle({
                    color: this.colors.getColor('Departure-Fork'),
                    weight: 2,
                    opacity: 0.7,
                    dashArray: '3, 15',
                  })
                  .arrowheads({ size: '5px', frequency: 2 });
              } else {
                this.waypointsTracksDeparture.get(key).setStyle({
                  color: this.colors.getColor('Departure-Fork'),
                  weight: 4,
                  opacity: 0.8,
                });
              }
            } else {
              if (key.stateTrajectory === TrajectoryState.ClosingMissionArea) {
                this.waypointsTracksDeparture
                  .get(key)
                  .setStyle({
                    color: this.colors.getColor('Departure'),
                    weight: 2,
                    opacity: 0.7,
                    dashArray: '3, 15',
                  })
                  .arrowheads({ size: '5px', frequency: 2 });
              } else {
                this.waypointsTracksDeparture.get(key).setStyle({
                  color: this.colors.getColor('Departure'),
                  weight: 4,
                  opacity: 0.8,
                });
              }
            }
          }
        });
      },
    );

    this.waypointsMission.forEach((value: L.Marker, key: AutopilotCommand) => {
      value.setIcon(this.icons.getIcon('Mission-' + key.apCommand));
      if (this.waypointsTracksMission.get(key)) {
        if (key.forkLabel) {
          if (key.stateTrajectory === TrajectoryState.ClosingMissionArea) {
            this.waypointsTracksMission
              .get(key)
              .setStyle({
                color: this.colors.getColor('Mission-Fork'),
                weight: 2,
                opacity: 0.7,
                dashArray: '3, 15',
              })
              .arrowheads({ size: '5px', frequency: 2 });
          } else {
            this.waypointsTracksMission.get(key).setStyle({
              color: this.colors.getColor('Mission-Fork'),
              weight: 4,
              opacity: 0.8,
            });
          }
        } else {
          if (key.stateTrajectory === TrajectoryState.ClosingMissionArea) {
            this.waypointsTracksMission
              .get(key)
              .setStyle({
                color: this.colors.getColor('Mission'),
                weight: 2,
                opacity: 0.7,
                dashArray: '3, 15',
              })
              .arrowheads({ size: '5px', frequency: 2 });
          } else {
            this.waypointsTracksMission.get(key).setStyle({
              color: this.colors.getColor('Mission'),
              weight: 4,
              opacity: 0.8,
            });
          }
        }
      }
    });

    this.waypointsArrival.forEach(
      (waypointVector: Map<AutopilotCommand, L.Marker>, branchName: string) => {
        waypointVector.forEach((value: L.Marker, key: AutopilotCommand) => {
          value.setIcon(this.icons.getIcon('Arrival-' + key.apCommand));
          if (this.waypointsTracksArrival.get(key)) {
            if (key.forkLabel) {
              if (key.stateTrajectory === TrajectoryState.ClosingMissionArea) {
                this.waypointsTracksArrival
                  .get(key)
                  .setStyle({
                    color: this.colors.getColor('Arrival-Fork'),
                    weight: 2,
                    opacity: 0.7,
                    dashArray: '3, 15',
                  })
                  .arrowheads({ size: '5px', frequency: 2 });
              } else {
                this.waypointsTracksArrival.get(key).setStyle({
                  color: this.colors.getColor('Arrival-Fork'),
                  weight: 4,
                  opacity: 0.8,
                });
              }
            } else {
              if (key.stateTrajectory === TrajectoryState.ClosingMissionArea) {
                this.waypointsTracksArrival
                  .get(key)
                  .setStyle({
                    color: this.colors.getColor('Arrival'),
                    weight: 2,
                    opacity: 0.7,
                    dashArray: '3, 15',
                  })
                  .arrowheads({ size: '5px', frequency: 2 });
              } else {
                this.waypointsTracksArrival.get(key).setStyle({
                  color: this.colors.getColor('Arrival'),
                  weight: 4,
                  opacity: 0.8,
                });
              }
            }
          }
        });
      },
    );
  }

  /**
   * Method to highlight the waypoints and tracks of the selected branch/fork. It will change the icon of the waypoints and the style of the tracks.
   * @param branch: branch/fork selected from the tree to be highlighted.
   * @param waypoint: waypoint to be highlighted.
   * @param isFork: boolean to know if the waypoint is the last in a fork or not, to highlight the last track of the fork but not he JoinRoute Waypoint.
   */
  highlightTrajectorySelection(
    branch: string,
    waypoint: AutopilotCommand,
    isFork: boolean,
  ) {
    switch (waypoint.stageLabel) {
      case 'Departure': {
        this.waypointsDeparture
          .get(branch)
          .get(waypoint)
          .setIcon(
            this.icons.getIcon('Departure-' + waypoint.apCommand + '-Big'),
          );
        if (this.waypointsTracksDeparture.get(waypoint))
          if (waypoint.forkLabel) {
            if (
              waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
            ) {
              this.waypointsTracksDeparture
                .get(waypoint)
                .setStyle({
                  color: this.colors.getColor('Departure-Fork'),
                  weight: 6,
                  opacity: 1,
                  dashArray: '3, 15',
                })
                .arrowheads({ size: '5px', frequency: 2 });
            } else {
              this.waypointsTracksDeparture.get(waypoint).setStyle({
                color: this.colors.getColor('Departure-Fork'),
                weight: 6,
                opacity: 1,
              });
            }
          } else {
            if (
              waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
            ) {
              this.waypointsTracksDeparture
                .get(waypoint)
                .setStyle({
                  color: this.colors.getColor('Departure'),
                  weight: 6,
                  opacity: 1,
                  dashArray: '3, 15',
                })
                .arrowheads({ size: '5px', frequency: 2 });
            } else {
              this.waypointsTracksDeparture.get(waypoint).setStyle({
                color: this.colors.getColor('Departure'),
                weight: 6,
                opacity: 1,
              });
            }
          }
        if (isFork) {
          let nextWaypoint = Array.from(
            this.waypointsDeparture.get(branch).keys(),
          )[
            Array.from(this.waypointsDeparture.get(branch).keys()).indexOf(
              waypoint,
            ) + 1
          ];
          if (nextWaypoint) {
            if (nextWaypoint.forkLabel) {
              this.waypointsTracksDeparture.get(nextWaypoint).setStyle({
                color: this.colors.getColor('Departure-Fork'),
                weight: 6,
                opacity: 1,
              });
            } else {
              this.waypointsTracksDeparture.get(nextWaypoint).setStyle({
                color: this.colors.getColor('Departure'),
                weight: 6,
                opacity: 1,
              });
            }
          }
        }
        break;
      }
      case 'Mission': {
        this.waypointsMission
          .get(waypoint)
          .setIcon(
            this.icons.getIcon('Mission-' + waypoint.apCommand + '-Big'),
          );
        if (this.waypointsTracksMission.get(waypoint))
          if (waypoint.forkLabel) {
            if (
              waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
            ) {
              this.waypointsTracksMission
                .get(waypoint)
                .setStyle({
                  color: this.colors.getColor('Mission-Fork'),
                  weight: 6,
                  opacity: 1,
                  dashArray: '3, 15',
                })
                .arrowheads({ size: '5px', frequency: 2 });
            } else {
              this.waypointsTracksMission.get(waypoint).setStyle({
                color: this.colors.getColor('Mission-Fork'),
                weight: 6,
                opacity: 1,
              });
            }
          } else {
            if (
              waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
            ) {
              this.waypointsTracksMission
                .get(waypoint)
                .setStyle({
                  color: this.colors.getColor('Mission'),
                  weight: 6,
                  opacity: 1,
                  dashArray: '3, 15',
                })
                .arrowheads({ size: '5px', frequency: 2 });
            } else {
              this.waypointsTracksMission.get(waypoint).setStyle({
                color: this.colors.getColor('Mission'),
                weight: 6,
                opacity: 1,
              });
            }
          }
        if (isFork) {
          let nextWaypoint = Array.from(this.waypointsMission.keys())[
            Array.from(this.waypointsMission.keys()).indexOf(waypoint) + 1
          ];
          if (nextWaypoint) {
            if (nextWaypoint.forkLabel) {
              this.waypointsTracksMission.get(nextWaypoint).setStyle({
                color: this.colors.getColor('Mission-Fork'),
                weight: 6,
                opacity: 1,
              });
            } else {
              this.waypointsTracksMission.get(nextWaypoint).setStyle({
                color: this.colors.getColor('Mission'),
                weight: 6,
                opacity: 1,
              });
            }
          }
        }
        break;
      }
      case 'Arrival': {
        this.waypointsArrival
          .get(branch)
          .get(waypoint)
          .setIcon(
            this.icons.getIcon('Arrival-' + waypoint.apCommand + '-Big'),
          );
        if (this.waypointsTracksArrival.get(waypoint))
          if (waypoint.forkLabel) {
            if (
              waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
            ) {
              this.waypointsTracksArrival
                .get(waypoint)
                .setStyle({
                  color: this.colors.getColor('Arrival-Fork'),
                  weight: 6,
                  opacity: 1,
                  dashArray: '3, 15',
                })
                .arrowheads({ size: '5px', frequency: 2 });
            } else {
              this.waypointsTracksArrival.get(waypoint).setStyle({
                color: this.colors.getColor('Arrival-Fork'),
                weight: 6,
                opacity: 1,
              });
            }
          } else {
            if (
              waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
            ) {
              this.waypointsTracksArrival
                .get(waypoint)
                .setStyle({
                  color: this.colors.getColor('Arrival'),
                  weight: 6,
                  opacity: 1,
                  dashArray: '3, 15',
                })
                .arrowheads({ size: '5px', frequency: 2 });
            } else {
              this.waypointsTracksArrival.get(waypoint).setStyle({
                color: this.colors.getColor('Arrival'),
                weight: 6,
                opacity: 1,
              });
            }
          }
        if (isFork) {
          let nextWaypoint = Array.from(
            this.waypointsArrival.get(branch).keys(),
          )[
            Array.from(this.waypointsArrival.get(branch).keys()).indexOf(
              waypoint,
            ) + 1
          ];
          if (nextWaypoint) {
            if (nextWaypoint.forkLabel) {
              this.waypointsTracksArrival.get(nextWaypoint).setStyle({
                color: this.colors.getColor('Arrival-Fork'),
                weight: 6,
                opacity: 1,
              });
            } else {
              this.waypointsTracksArrival.get(nextWaypoint).setStyle({
                color: this.colors.getColor('Arrival'),
                weight: 6,
                opacity: 1,
              });
            }
          }
        }
        break;
      }
    }
  }

  /**
   * Method to add a new waypoint to the vectors and the tree. It will create the waypoint and add it to the map and the tree. It will also
   * create the track between the last waypoint and the new one and refresh the joints between stages.
   * @param point: coordinates of the click on the map to create the waypoint.
   * @returns: the waypoint created in AutopilotCommand format.
   */
  createWaypoint(point: L.LatLng): AutopilotCommand {
    try {
      let waypoint: AutopilotCommand;
      if (this.currentWaypointType === AutopilotCommandEnum.MissionArea) {
        waypoint = this.trajectoryUtils.obtainAPCommand(
          point.lat,
          point.lng,
          this.getAltitude(),
          this.getSpeed(),
          this.currentWaypointType,
          this.currentState,
          this.currentStage,
          this.currentWaypointType,
          this.currentBranch,
          this.region,
        );
      } else {
        waypoint = this.trajectoryUtils.obtainAPCommand(
          point.lat,
          point.lng,
          this.getAltitude(),
          this.getSpeed(),
          this.currentWaypointType,
          this.currentState,
          this.currentStage,
          this.currentWaypointType,
          this.currentBranch,
          null,
        );
      }

      if (
        this.currentWaypointType === AutopilotCommandEnum.JoinRoute ||
        this.isAddingFork ||
        this.isFirstFork
      ) {
        waypoint.forkLabel = 'Fork ' + (this.trajectoryTree.forkCounter - 1);
      }

      if (
        !this.trajectoryTree.addWaypoint(
          this.currentStage,
          this.currentState,
          waypoint,
          this.currentBranch,
        )
      ) {
        return null;
      }

      switch (this.currentStage) {
        case 'Departure': {
          this.createWaypointPerStage(
            point,
            waypoint,
            this.waypointsDeparture.get(this.currentBranch),
            this.waypointsTracksDeparture,
            this.regionsDeparture,
          );
          break;
        }
        case 'Mission': {
          this.createWaypointPerStage(
            point,
            waypoint,
            this.waypointsMission,
            this.waypointsTracksMission,
            this.regionsMission,
          );
          break;
        }
        case 'Arrival': {
          this.createWaypointPerStage(
            point,
            waypoint,
            this.waypointsArrival.get(this.currentBranch),
            this.waypointsTracksArrival,
            this.regionsArrival,
          );
          break;
        }
      }

      if (this.currentState === TrajectoryState.CreatingArrivalTrack) {
        this.changeState(TrajectoryState.InArrivalTrack);
      }

      if (!this.isAddingFork) {
        this.refreshStageJoints();
      }

      this.ref.detectChanges();

      return waypoint;
    } catch (err) {
      console.log('Error printing waypoint: ' + err);
      return null;
    }
  }

  /**
   * Method to create the marker, the popup associated to the waypoint and the methods to allow dragging the waypoint. It will also create the track
   * between the last waypoint and the new one and refresh the joints between stages. Depending on the case the track will be added differently (check code)
   * @param point: coordinates of the click on the map to create the waypoint.
   * @param waypoint: waypoint with the generic information (label, coordinates, speed, altitude, etc.)
   * @param waypointVector: vector of waypoints (Map<AutopilotCommand, L.Marker>) to add the new waypoint to.
   * @param trackVector: vector of tracks (Map<AutopilotCommand, L.Polyline>) to add the new track to.
   */
  createWaypointPerStage(
    point: L.LatLng,
    waypoint: AutopilotCommand,
    waypointVector: Map<AutopilotCommand, L.Marker>,
    trackVector: Map<AutopilotCommand, L.Polyline>,
    regionsVector: Map<AutopilotCommand, L.Polygon>,
  ) {
    const waypoint_position = point;
    const marker = L.marker(waypoint_position, {
      icon: this.icons.getIcon(
        this.currentStage + '-' + this.currentWaypointType,
      ),
      draggable: true,
    }).bindPopup(this.obtainMarkerPopup(waypoint, this.translateService), {
      className: 'custom-popup',
    });

    marker.bindTooltip(
      `${waypoint.apCommand} · ${waypoint.stageLabel}<br>Alt: ${waypoint.point.altitude}m`,
      { permanent: false, direction: 'top', opacity: 0.9 }
    );

    marker.on('click', () => {
      const currentZoom = this.leafletmap.getZoom();
      this.leafletmap.setView(waypoint_position, currentZoom, {
        animate: true,
        duration: 0.5,
      });
      this.trajectoryTree.scrollToWaypoint(waypoint);
    });

    marker.on('drag', (e) => {
      if (
        waypoint.stateTrajectory === TrajectoryState.CreatingMissionArea ||
        waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea ||
        waypoint.stateTrajectory ===
          TrajectoryState.CreatingMissionAreaInFork ||
        waypoint.stateTrajectory === TrajectoryState.ClosingMissionAreaInFork
      ) {
        const rset = regionsVector
          .get(waypoint)
          .getLatLngs()[0] as Array<L.LatLng>;
        if (this.trajectoryUtils.isInRegion(e.target.getLatLng(), rset)) {
          this.adaptTracksAfterWaypointMoved(
            waypoint,
            waypointVector,
            trackVector,
            regionsVector,
            e.target.getLatLng(),
            this.tracksLayer,
            this.trajectoryTree,
            this.lastWaypoint,
          );
          this.refreshStageJoints();
        }
      } else {
        this.adaptTracksAfterWaypointMoved(
          waypoint,
          waypointVector,
          trackVector,
          regionsVector,
          e.target.getLatLng(),
          this.tracksLayer,
          this.trajectoryTree,
          this.lastWaypoint,
        );
        this.refreshStageJoints();
      }
    });

    marker.on('dragend', (e) => {
      if (
        waypoint.stateTrajectory === TrajectoryState.CreatingMissionArea ||
        waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea ||
        waypoint.stateTrajectory ===
          TrajectoryState.CreatingMissionAreaInFork ||
        waypoint.stateTrajectory === TrajectoryState.ClosingMissionAreaInFork
      ) {
        const rset = regionsVector
          .get(waypoint)
          .getLatLngs()[0] as Array<L.LatLng>;
        if (this.trajectoryUtils.isInRegion(e.target.getLatLng(), rset)) {
          marker.bindPopup(
            this.obtainMarkerPopup(waypoint, this.translateService),
            { className: 'custom-popup' },
          );
          this.adaptTracksAfterWaypointMoved(
            waypoint,
            waypointVector,
            trackVector,
            regionsVector,
            e.target.getLatLng(),
            this.tracksLayer,
            this.trajectoryTree,
            this.lastWaypoint,
          );
          this.refreshStageJoints();
        } else {
          let lastPoint: L.LatLng = new L.LatLng(
            waypoint.point.latitude,
            waypoint.point.longitude,
          );
          marker.setLatLng(lastPoint);
          this.toastService.presentToast(
            this.translateService.instant('FP.TOAST.OUT_AREA'),
            'bottom',
            5000,
            '',
            'warning',
          );
        }
      } else {
        marker.bindPopup(
          this.obtainMarkerPopup(waypoint, this.translateService),
          { className: 'custom-popup' },
        );
        this.adaptTracksAfterWaypointMoved(
          waypoint,
          waypointVector,
          trackVector,
          regionsVector,
          e.target.getLatLng(),
          this.tracksLayer,
          this.trajectoryTree,
          this.lastWaypoint,
        );
        this.refreshStageJoints();
      }
      this.ref.detectChanges();
    });

    // We will add the track only if there is already a waypoint in the vector and we are not in the CreatingInitialTrack or CreatingArrivalTrack state
    // (so at the first waypoint) and also we must not be in the first waypoint of a new fork.
    if (
      waypointVector.size > 0 &&
      this.currentState !== TrajectoryState.CreatingInitialTrack &&
      this.currentState !== TrajectoryState.CreatingArrivalTrack &&
      !this.currentFork
    ) {
      let latitude = (
        this.getLastKey(waypointVector) as AutopilotCommand
      ).point.latitude;
      let longitude = (
        this.getLastKey(waypointVector) as AutopilotCommand
      ).point.longitude;
      const track = L.polyline([
        new L.LatLng(latitude, longitude),
        new L.LatLng(point.lat, point.lng),
      ]);
      if (waypoint.forkLabel) {
        if (this.currentState === TrajectoryState.ClosingMissionAreaInFork) {
          track.setStyle({
            color: this.colors.getColor(this.currentStage + '-Fork'),
            weight: 4,
            opacity: 0.8,
            dashArray: '3, 4.5',
          });
        } else {
          track
            .setStyle({
              color: this.colors.getColor(this.currentStage + '-Fork'),
              weight: 4,
              opacity: 0.8,
            })
            .arrowheads({ size: '12px', frequency: 2 });
        }
      } else {
        if (this.currentState === TrajectoryState.ClosingMissionArea) {
          track.setStyle({
            color: this.colors.getColor(this.currentStage),
            weight: 4,
            opacity: 0.8,
            dashArray: '3, 4.5',
          });
        } else {
          track
            .setStyle({
              color: this.colors.getColor(this.currentStage),
              weight: 4,
              opacity: 0.8,
            })
            .arrowheads({ size: '12px', frequency: 2 });
        }
      }
      track.on('click', (e: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(e);
        if (
          waypoint.stateTrajectory !== TrajectoryState.ClosingMissionArea &&
          waypoint.stateTrajectory !== TrajectoryState.ClosingMissionAreaInFork
        )
          this.addMiddleWaypoint(waypoint, e.latlng);
      });
      this.tracksLayer.addLayer(track);
      trackVector.set(waypoint, track);
    }

    // For the case where we are at the first waypoint of a new fork track we will create the track from the ForkRoute waypoint associated to the fork.
    if (
      (this.currentState === TrajectoryState.CreatingForkTrack ||
        this.currentState === TrajectoryState.CreatingMissionAreaInFork) &&
      this.currentFork
    ) {
      let latitude = this.currentFork.point.latitude;
      let longitude = this.currentFork.point.longitude;
      const track = L.polyline([
        new L.LatLng(latitude, longitude),
        new L.LatLng(point.lat, point.lng),
      ]).arrowheads({ size: '12px', frequency: 2 });

      track.setStyle({
        color: this.colors.getColor(this.currentStage + '-Fork'),
        weight: 4,
        opacity: 0.8,
      });
      track.on('click', (e: L.LeafletMouseEvent) => {
        L.DomEvent.stopPropagation(e);
        this.addMiddleWaypoint(waypoint, e.latlng);
      });

      this.tracksLayer.addLayer(track);
      trackVector.set(waypoint, track);
      this.currentFork = null;
    }

    // If we are in a Fork we must add the waypoint after the fork route or after the last waypoint in the fork
    waypointVector.set(waypoint, marker);
    this.markersLayer.addLayer(marker);

    if (
      this.currentState === TrajectoryState.CreatingMissionArea ||
      this.currentState === TrajectoryState.ClosingMissionArea ||
      this.currentState === TrajectoryState.CreatingMissionAreaInFork ||
      this.currentState === TrajectoryState.ClosingMissionAreaInFork
    ) {
      switch (this.currentStage) {
        case 'Departure': {
          this.regionsDeparture.set(waypoint, this.regionPolygon);
          break;
        }
        case 'Mission': {
          this.regionsMission.set(waypoint, this.regionPolygon);
          break;
        }
        case 'Arrival': {
          this.regionsArrival.set(waypoint, this.regionPolygon);
          break;
        }
      }
    }
  }

  /**
   * Method to create a new waypoint in the middle of a track. It will create the marker, the popup associated to the waypoint and the methods to allow
   * dragging the waypoint. It also will modify the previous track.
   * @param followingWaypoint: last waypoint of the track clicked, so the next waypoint of the new to be created.
   * @param point: coordinates of the click on the map to create the waypoint.
   */
  addMiddleWaypoint(followingWaypoint: AutopilotCommand, point: L.LatLng) {
    let previousWaypoint;
    switch (followingWaypoint.stageLabel) {
      case 'Departure': {
        previousWaypoint = this.obtainPreviousWaypoint(
          this.waypointsTracksDeparture
            .get(followingWaypoint)
            .getLatLngs()[0] as L.LatLng,
        );
        break;
      }
      case 'Mission': {
        previousWaypoint = this.obtainPreviousWaypoint(
          this.waypointsTracksMission
            .get(followingWaypoint)
            .getLatLngs()[0] as L.LatLng,
        );
        break;
      }
      case 'Arrival': {
        previousWaypoint = this.obtainPreviousWaypoint(
          this.waypointsTracksArrival
            .get(followingWaypoint)
            .getLatLngs()[0] as L.LatLng,
        );
        break;
      }
    }

    let waypoint: AutopilotCommand = {} as AutopilotCommand;
    waypoint.forkLabel = followingWaypoint?.forkLabel;

    if (
      followingWaypoint.apCommand === 'JoinRoute' ||
      followingWaypoint.apCommand === 'ForkRoute' ||
      followingWaypoint.apCommand === 'Land'
    ) {
      if (previousWaypoint.apCommand === 'Takeoff') {
        waypoint.stateTrajectory = TrajectoryState.InInitialTrack;
      } else if (
        previousWaypoint.stateTrajectory ===
        TrajectoryState.ClosingMissionAreaInFork
      ) {
        waypoint.stateTrajectory = TrajectoryState.InForkTrack;
      } else if (
        previousWaypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
      ) {
        waypoint.stateTrajectory = followingWaypoint.stateTrajectory;
      } else {
        waypoint.stateTrajectory = previousWaypoint.stateTrajectory;
      }
      if (followingWaypoint.apCommand === 'JoinRoute') {
        waypoint.forkLabel = previousWaypoint.forkLabel;
      }
    } else if (followingWaypoint.apCommand === 'MissionArea') {
      if (previousWaypoint.apCommand === 'Takeoff') {
        waypoint.stateTrajectory = TrajectoryState.InInitialTrack;
      } else if (previousWaypoint.apCommand === 'ForkRoute') {
        waypoint.stateTrajectory = TrajectoryState.InForkTrack;
        waypoint.forkLabel = followingWaypoint.forkLabel;
      } else {
        waypoint.stateTrajectory = previousWaypoint.stateTrajectory;
      }
    } else {
      waypoint.stateTrajectory = followingWaypoint.stateTrajectory;
    }

    waypoint.stageLabel = followingWaypoint.stageLabel;
    waypoint.apCommand = AutopilotCommandEnum.SetWaypoint;
    waypoint.branchLabel = followingWaypoint.branchLabel;
    waypoint.label = AutopilotCommandEnum.SetWaypoint;
    waypoint.speed = this.getSpeed();
    waypoint.speedType = AutopilotSpeedTypeEnum.Ground;
    waypoint.speedUnits = AutopilotSpeedUnitsEnum.ms;
    waypoint.point = {
      latitude: parseFloat(point.lat.toFixed(7)),
      longitude: parseFloat(point.lng.toFixed(7)),
      altitude: this.getAltitude(),
      altitudeType: AutopilotAltitudeTypeEnum.agl,
      altitudeUnits: AutopilotAltitudeUnitsEnum.meters,
    };

    if (
      !this.trajectoryTree.addWaypointAtIndex(
        waypoint.stageLabel,
        waypoint,
        followingWaypoint,
        previousWaypoint,
      )
    )
      return;

    switch (waypoint.stageLabel) {
      case 'Departure': {
        this.addMiddleWaypointPerStage(
          followingWaypoint,
          waypoint,
          point,
          this.waypointsDeparture.get(waypoint.branchLabel),
          this.waypointsTracksDeparture,
          this.regionsDeparture,
        );
        this.tracksLayer.removeLayer(
          this.waypointsTracksDeparture.get(followingWaypoint),
        );
        this.waypointsTracksDeparture
          .get(followingWaypoint)
          .setLatLngs([
            point,
            this.waypointsTracksDeparture
              .get(followingWaypoint)
              .getLatLngs()[1] as L.LatLng,
          ]);
        this.tracksLayer.addLayer(
          this.waypointsTracksDeparture.get(followingWaypoint),
        );
        break;
      }
      case 'Mission': {
        this.addMiddleWaypointPerStage(
          followingWaypoint,
          waypoint,
          point,
          this.waypointsMission,
          this.waypointsTracksMission,
          this.regionsMission,
        );
        this.tracksLayer.removeLayer(
          this.waypointsTracksMission.get(followingWaypoint),
        );
        this.waypointsTracksMission
          .get(followingWaypoint)
          .setLatLngs([
            point,
            this.waypointsTracksMission
              .get(followingWaypoint)
              .getLatLngs()[1] as L.LatLng,
          ]);
        this.tracksLayer.addLayer(
          this.waypointsTracksMission.get(followingWaypoint),
        );
        break;
      }
      case 'Arrival': {
        this.addMiddleWaypointPerStage(
          followingWaypoint,
          waypoint,
          point,
          this.waypointsArrival.get(waypoint.branchLabel),
          this.waypointsTracksArrival,
          this.regionsArrival,
        );
        this.tracksLayer.removeLayer(
          this.waypointsTracksArrival.get(followingWaypoint),
        );
        this.waypointsTracksArrival
          .get(followingWaypoint)
          .setLatLngs([
            point,
            this.waypointsTracksArrival
              .get(followingWaypoint)
              .getLatLngs()[1] as L.LatLng,
          ]);
        this.tracksLayer.addLayer(
          this.waypointsTracksArrival.get(followingWaypoint),
        );
        break;
      }
    }
  }

  /**
   * This method is used while adding a waypoint in the middle of a track to recover the previous waypoint of the new to be created.
   * @param point: coordinates of the waypoint to be found.
   * @returns: the previous waypoint in AutopilotCommand format.
   */
  obtainPreviousWaypoint(point: L.LatLng): AutopilotCommand {
    let previousWaypoint;
    this.trajectoryTree.tree.obtainArray().forEach((item: PlanningItem) => {
      if (item.items) {
        item.items.forEach((subitem: PlanningItem) => {
          if (subitem.items) {
            subitem.items.forEach((subsubitem: PlanningItem) => {
              if (
                subsubitem.point.latitude === point.lat &&
                subsubitem.point.longitude === point.lng
              ) {
                previousWaypoint = subsubitem;
              }
            });
          } else if (
            subitem.point.latitude === point.lat &&
            subitem.point.longitude === point.lng
          ) {
            previousWaypoint = subitem;
          }
        });
      }
    });
    return previousWaypoint;
  }

  /**
   * Method to create the waypoint in the middle of a track and added to the tree and vectors, and also add the marker, the track and
   * the methods to allow dragging the waypoint.
   * @param nextWaypoint: next waypoint of the track clicked, so the previous waypoint of the new to be created.
   * @param waypoint: waypoint with the generic information (label, coordinates, speed, altitude, etc.)
   * @param point: coordinates of the click on the map to create the waypoint.
   * @param waypointVector: vector of waypoints (Map<AutopilotCommand, L.Marker>) to add the new waypoint to.
   * @param trackVector: vector of tracks (Map<AutopilotCommand, L.Polyline>) to add the new track to.
   */
  addMiddleWaypointPerStage(
    nextWaypoint: AutopilotCommand,
    waypoint: AutopilotCommand,
    point: L.LatLng,
    waypointVector: Map<AutopilotCommand, L.Marker>,
    trackVector: Map<AutopilotCommand, L.Polyline>,
    regionsVector: Map<AutopilotCommand, L.Polygon>,
  ) {
    const waypoint_position = point;
    const marker = L.marker(waypoint_position, {
      icon: this.icons.getIcon(
        waypoint.stageLabel + '-' + AutopilotCommandEnum.SetWaypoint,
      ),
      draggable: true,
    }).bindPopup(this.obtainMarkerPopup(waypoint, this.translateService), {
      className: 'custom-popup',
    });

    marker.bindTooltip(
      `${waypoint.apCommand} · ${waypoint.stageLabel}<br>Alt: ${waypoint.point.altitude}m`,
      { permanent: false, direction: 'top', opacity: 0.9 }
    );

    marker.on('click', () => {
      const currentZoom = this.leafletmap.getZoom();
      this.leafletmap.setView(waypoint_position, currentZoom, {
        animate: true,
        duration: 0.5,
      });
      this.trajectoryTree.scrollToWaypoint(waypoint);
    });

    marker.on('drag', (e) => {
      if (
        waypoint.stateTrajectory === TrajectoryState.CreatingMissionArea ||
        waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
      ) {
        const rset = regionsVector
          .get(waypoint)
          .getLatLngs()[0] as Array<L.LatLng>;
        if (this.trajectoryUtils.isInRegion(e.target.getLatLng(), rset)) {
          this.adaptTracksAfterWaypointMoved(
            waypoint,
            waypointVector,
            trackVector,
            regionsVector,
            e.target.getLatLng(),
            this.tracksLayer,
            this.trajectoryTree,
            this.lastWaypoint,
          );
          this.refreshStageJoints();
        }
      } else {
        this.adaptTracksAfterWaypointMoved(
          waypoint,
          waypointVector,
          trackVector,
          regionsVector,
          e.target.getLatLng(),
          this.tracksLayer,
          this.trajectoryTree,
          this.lastWaypoint,
        );
        this.refreshStageJoints();
      }
    });

    marker.on('dragend', (e) => {
      if (
        waypoint.stateTrajectory === TrajectoryState.CreatingMissionArea ||
        waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
      ) {
        const rset = regionsVector
          .get(waypoint)
          .getLatLngs()[0] as Array<L.LatLng>;
        if (this.trajectoryUtils.isInRegion(e.target.getLatLng(), rset)) {
          marker.bindPopup(
            this.obtainMarkerPopup(waypoint, this.translateService),
            { className: 'custom-popup' },
          );
          this.adaptTracksAfterWaypointMoved(
            waypoint,
            waypointVector,
            trackVector,
            regionsVector,
            e.target.getLatLng(),
            this.tracksLayer,
            this.trajectoryTree,
            this.lastWaypoint,
          );
          this.refreshStageJoints();
        } else {
          let lastPoint: L.LatLng = new L.LatLng(
            waypoint.point.latitude,
            waypoint.point.longitude,
          );
          marker.setLatLng(lastPoint);
          this.toastService.presentToast(
            this.translateService.instant('FP.TOAST.OUT_AREA'),
            'bottom',
            5000,
            '',
            'warning',
          );
        }
      } else {
        marker.bindPopup(
          this.obtainMarkerPopup(waypoint, this.translateService),
          { className: 'custom-popup' },
        );
        this.adaptTracksAfterWaypointMoved(
          waypoint,
          waypointVector,
          trackVector,
          regionsVector,
          e.target.getLatLng(),
          this.tracksLayer,
          this.trajectoryTree,
          this.lastWaypoint,
        );
        this.refreshStageJoints();
      }
      this.ref.detectChanges();
    });

    // Add new track
    const track = L.polyline([
      trackVector.get(nextWaypoint).getLatLngs()[0] as L.LatLng,
      point,
    ]).arrowheads({ size: '12px', frequency: 2 });
    if (waypoint.forkLabel) {
      track.setStyle({
        color: this.colors.getColor(this.currentStage + '-Fork'),
        weight: 4,
        opacity: 0.8,
      });
    } else {
      track.setStyle({
        color: this.colors.getColor(waypoint.stageLabel),
        weight: 4,
        opacity: 0.8,
      });
    }
    track.on('click', (e: L.LeafletMouseEvent) => {
      L.DomEvent.stopPropagation(e);
      this.addMiddleWaypoint(waypoint, e.latlng);
    });
    this.tracksLayer.addLayer(track);

    this.insertWaypointInVector(nextWaypoint, waypoint, track, trackVector);
    this.insertWaypointInVector(nextWaypoint, waypoint, marker, waypointVector);
    this.markersLayer.addLayer(marker);
  }

  /**
   * Method to insert a new waypoint in a specific location of a vector.
   * @param followingWaypoint: waypoint to be followed by the new waypoint.
   * @param waypoint: new waypoint to be inserted.
   * @param object: object to be inserted in the Map (could be Marker or Polyline).
   * @param vector: vector to insert the new waypoint.
   */
  insertWaypointInVector(
    followingWaypoint: AutopilotCommand,
    waypoint: AutopilotCommand,
    object,
    vector,
  ) {
    let index = Array.from(vector.keys()).indexOf(followingWaypoint);
    let array = Array.from(vector.keys());
    array.splice(index, 0, waypoint);
    let newWaypointVector = new Map<AutopilotCommand, L.Marker>();
    array.forEach((key: AutopilotCommand) => {
      if (key === waypoint) {
        newWaypointVector.set(waypoint, object);
      } else {
        newWaypointVector.set(key, vector.get(key));
      }
    });
    vector.clear();
    newWaypointVector.forEach((value: L.Marker, key: AutopilotCommand) => {
      vector.set(key, value);
    });
  }

  /**
   * Edit information of a waypoint, it will update the waypoint vector and track if coordinates changed and also marker and popup content.
   * @param autopilotCommand: waypoint to be edited.
   */
  editWaypoint(autopilotCommand: AutopilotCommand) {
    // Pensar lògica per el JoinRoute

    // Update waypoint vector and track if coordinates changed and also marker and popup content.
    switch (autopilotCommand.stageLabel) {
      case 'Departure': {
        this.waypointsDeparture
          .get(autopilotCommand.branchLabel)
          .get(autopilotCommand)
          .setLatLng(
            new L.LatLng(
              autopilotCommand.point.latitude,
              autopilotCommand.point.longitude,
            ),
          );
        this.waypointsDeparture
          .get(autopilotCommand.branchLabel)
          .get(autopilotCommand)
          .setPopupContent(
            this.obtainMarkerPopup(autopilotCommand, this.translateService),
          );

        this.adaptTracksAfterWaypointMoved(
          autopilotCommand,
          this.waypointsDeparture.get(autopilotCommand.branchLabel),
          this.waypointsTracksDeparture,
          this.regionsDeparture,
          new L.LatLng(
            autopilotCommand.point.latitude,
            autopilotCommand.point.longitude,
          ),
          this.tracksLayer,
          this.trajectoryTree,
          this.lastWaypoint,
        );

        this.ref.detectChanges();
        this.updateTree();
        break;
      }
      case 'Mission': {
        this.waypointsMission
          .get(autopilotCommand)
          .setLatLng(
            new L.LatLng(
              autopilotCommand.point.latitude,
              autopilotCommand.point.longitude,
            ),
          );
        this.waypointsMission
          .get(autopilotCommand)
          .setPopupContent(
            this.obtainMarkerPopup(autopilotCommand, this.translateService),
          );

        this.adaptTracksAfterWaypointMoved(
          autopilotCommand,
          this.waypointsMission,
          this.waypointsTracksMission,
          this.regionsMission,
          new L.LatLng(
            autopilotCommand.point.latitude,
            autopilotCommand.point.longitude,
          ),
          this.tracksLayer,
          this.trajectoryTree,
          this.lastWaypoint,
        );

        this.ref.detectChanges();
        this.updateTree();
        break;
      }
      case 'Arrival': {
        this.waypointsArrival
          .get(autopilotCommand.branchLabel)
          .get(autopilotCommand)
          .setLatLng(
            new L.LatLng(
              autopilotCommand.point.latitude,
              autopilotCommand.point.longitude,
            ),
          );
        this.waypointsArrival
          .get(autopilotCommand.branchLabel)
          .get(autopilotCommand)
          .setPopupContent(
            this.obtainMarkerPopup(autopilotCommand, this.translateService),
          );

        this.adaptTracksAfterWaypointMoved(
          autopilotCommand,
          this.waypointsArrival.get(autopilotCommand.branchLabel),
          this.waypointsTracksArrival,
          this.regionsArrival,
          new L.LatLng(
            autopilotCommand.point.latitude,
            autopilotCommand.point.longitude,
          ),
          this.tracksLayer,
          this.trajectoryTree,
          this.lastWaypoint,
        );

        this.ref.detectChanges();
        this.updateTree();
        break;
      }
    }
  }

  /**
   * Method called when a waypoint is clicked on the tree. It will show the waypoint on the map and center the view on it.
   * @param waypoint: waypoint to be shown on the map.
   */
  showWaypointOnMap(waypoint: AutopilotCommand) {
    switch (waypoint.stageLabel) {
      case 'Departure': {
        this.waypointsDeparture
          .get(waypoint.branchLabel)
          .get(waypoint)
          .openPopup();
        break;
      }
      case 'Mission': {
        this.waypointsMission.get(waypoint).openPopup();
        break;
      }
      case 'Arrival': {
        this.waypointsArrival
          .get(waypoint.branchLabel)
          .get(waypoint)
          .openPopup();
        break;
      }
    }
    this.leafletmap.setView(
      new L.LatLng(waypoint.point.latitude, waypoint.point.longitude),
      this.leafletmap.getZoom(),
      { animate: true, duration: 0.5 },
    );
  }

  /**
   * Method to remove a waypoint from the trajectory. The waypoint will be removed from the map and the tree. It will also remove the track and the
   * marker associated to it. And also from the waypoint vector of the specific stage.
   * @param apCommand: waypoint to be removed.
   * @param waypointVector: vector of waypoints (Map<AutopilotCommand, L.Marker>) to remove the waypoint from. In case of Departure or Arrival Stage
   *                        the selection of which initial or arrival has been done before.
   * @param trackVector: vector of tracks (Map<AutopilotCommand, L.Polyline>) to remove the track from.
   * @param removeJoint: boolean specifying if the JoinRoute Waypoint has to be removed or not. Only if we are removing added fork tracks this will be
   *                    true. SPECIAL CASE
   */
  removeWaypoint(
    apCommand: AutopilotCommand,
    waypointVector: Map<AutopilotCommand, L.Marker>,
    trackVector: Map<AutopilotCommand, L.Polyline>,
    regionsVector: Map<AutopilotCommand, L.Polygon>,
    removeJoint: boolean,
  ) {
    if (trackVector.get(apCommand))
      this.tracksLayer.removeLayer(trackVector.get(apCommand));
    if (
      !apCommand.label.includes('LastWaypoint') &&
      apCommand.apCommand !== 'JoinRoute'
    ) {
      this.markersLayer.removeLayer(waypointVector.get(apCommand));
      waypointVector.delete(apCommand);
    } else if (
      apCommand.label.includes('LastWaypoint') &&
      apCommand.apCommand === 'JoinRoute'
    ) {
      this.markersLayer.removeLayer(waypointVector.get(apCommand));
      waypointVector.delete(apCommand);
    } else if (removeJoint) {
      this.markersLayer.removeLayer(waypointVector.get(apCommand));
      waypointVector.delete(apCommand);
    }
    if (
      apCommand.apCommand === 'MissionArea' ||
      apCommand.stateTrajectory === TrajectoryState.ClosingMissionArea ||
      apCommand.stateTrajectory === TrajectoryState.ClosingMissionAreaInFork
    ) {
      if (apCommand.stateTrajectory === TrajectoryState.CreatingMissionArea) {
        this.polygonLayers.removeLayer(regionsVector.get(apCommand));
      }
      regionsVector.delete(apCommand);
    }
    trackVector.delete(apCommand);
  }

  /**
   * Method to remove a specific waypoint from the vectors and the tree, this method is called from the left panel by clicking the x icon.
   * @param waypoint: waypoint to be removed.
   */
  removeSpecificWaypoint(waypoint: AutopilotCommand) {
    this.attenuateTrajectory();
    let nextWaypoint;

    switch (waypoint.stageLabel) {
      case 'Departure': {
        // Obtain next waypoint
        nextWaypoint = Array.from(
          this.waypointsDeparture.get(waypoint.branchLabel).keys(),
        )[
          Array.from(
            this.waypointsDeparture.get(waypoint.branchLabel).keys(),
          ).indexOf(waypoint) + 1
        ];

        if (waypoint.forkLabel) {
          if (
            this.trajectoryTree.obtainWaypointsAtFork(
              waypoint.branchLabel,
              waypoint.forkLabel,
            ) == 1
          ) {
            this.presentRemovalForkConfirmation(waypoint);
          } else {
            this.removeSpecificWaypointPerStage(
              waypoint,
              nextWaypoint,
              this.waypointsDeparture.get(waypoint.branchLabel),
              this.waypointsTracksDeparture,
            );
          }
        } else {
          this.removeSpecificWaypointPerStage(
            waypoint,
            nextWaypoint,
            this.waypointsDeparture.get(waypoint.branchLabel),
            this.waypointsTracksDeparture,
          );
        }

        break;
      }
      case 'Mission': {
        nextWaypoint = Array.from(this.waypointsMission.keys())[
          Array.from(this.waypointsMission.keys()).indexOf(waypoint) + 1
        ];

        if (waypoint.forkLabel) {
          if (
            this.trajectoryTree.obtainWaypointsAtFork(
              waypoint.stageLabel,
              waypoint.forkLabel,
            ) == 1
          ) {
            this.presentRemovalForkConfirmation(waypoint);
          } else {
            this.removeSpecificWaypointPerStage(
              waypoint,
              nextWaypoint,
              this.waypointsMission,
              this.waypointsTracksMission,
            );
          }
        } else {
          this.removeSpecificWaypointPerStage(
            waypoint,
            nextWaypoint,
            this.waypointsMission,
            this.waypointsTracksMission,
          );
        }

        break;
      }
      case 'Arrival': {
        nextWaypoint = Array.from(
          this.waypointsArrival.get(waypoint.branchLabel).keys(),
        )[
          Array.from(
            this.waypointsArrival.get(waypoint.branchLabel).keys(),
          ).indexOf(waypoint) + 1
        ];

        if (waypoint.forkLabel) {
          if (
            this.trajectoryTree.obtainWaypointsAtFork(
              waypoint.branchLabel,
              waypoint.forkLabel,
            ) == 1
          ) {
            this.presentRemovalForkConfirmation(waypoint);
          } else {
            this.removeSpecificWaypointPerStage(
              waypoint,
              nextWaypoint,
              this.waypointsArrival.get(waypoint.branchLabel),
              this.waypointsTracksArrival,
            );
          }
        } else {
          this.removeSpecificWaypointPerStage(
            waypoint,
            nextWaypoint,
            this.waypointsArrival.get(waypoint.branchLabel),
            this.waypointsTracksArrival,
          );
        }

        break;
      }
    }

    this.currentSelection = null;
    this.refreshStageJoints();
    this.ref.detectChanges();
  }

  /**
   * Method called from removeSpecificWaypoint and passing the variables needed, depending on the stage, to remove the waypoint and adapt the tracks involved.
   * @param waypoint: waypoint to be removed.
   * @param nextWaypoint: next waypoint after the waypoint to be removed.
   * @param waypointVector: vector of waypoints (Map<AutopilotCommand, L.Marker>) to remove the waypoint from.
   * @param trackVector: vector of tracks (Map<AutopilotCommand, L.Polyline>) to remove or modify the tracks involved from.
   */
  removeSpecificWaypointPerStage(
    waypoint: AutopilotCommand,
    nextWaypoint: AutopilotCommand,
    waypointVector: Map<AutopilotCommand, L.Marker>,
    trackVector: Map<AutopilotCommand, L.Polyline>,
  ) {
    if (
      waypoint.stateTrajectory === TrajectoryState.CreatingMissionArea ||
      waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea ||
      waypoint.stateTrajectory === TrajectoryState.CreatingMissionAreaInFork ||
      waypoint.stateTrajectory === TrajectoryState.ClosingMissionAreaInFork
    ) {
      this.presentRemovalMissionAreaConfirmation(waypoint);
    } else {
      // Adapt next waypoint track due to removal
      if (nextWaypoint) {
        this.tracksLayer.removeLayer(trackVector.get(nextWaypoint));
        if (trackVector.get(waypoint)) {
          trackVector
            .get(nextWaypoint)
            .setLatLngs([
              trackVector.get(waypoint).getLatLngs()[0] as L.LatLng,
              trackVector.get(nextWaypoint).getLatLngs()[1] as L.LatLng,
            ]);
          this.tracksLayer.addLayer(trackVector.get(nextWaypoint));
        } else {
          trackVector.delete(nextWaypoint);
        }
      }

      if (trackVector.get(waypoint)) {
        this.tracksLayer.removeLayer(trackVector.get(waypoint));
        trackVector.delete(waypoint);
      }

      this.markersLayer.removeLayer(waypointVector.get(waypoint));

      // Remove waypoint from the vector
      if (waypoint.apCommand === 'Land') {
        this.changeStageDirectly('Arrival');
      }
      waypointVector.delete(waypoint);

      // Remove waypoint from the tree
      this.trajectoryTree.removeWaypointFromTree(waypoint);
    }
  }

  /**
   * Method to print the selected region on the map. It will draw the polygon with the coordinates of the region selected.
   * @param region: region to be drawn on the map.
   */
  drawRegionSelected(region: Region) {
    if (
      this.currentState === TrajectoryState.CreatingForkTrack ||
      this.currentState === TrajectoryState.InForkTrack
    ) {
      this.changeState(TrajectoryState.CreatingMissionAreaInFork);
      const rset = (region.rset as any).map(
        (e) => new L.LatLng(e.latitude, e.longitude),
      );
      this.regionPolygon = new L.Polygon(rset, {
        color: this.colors.getColor(this.currentStage + '-Fork'),
        weight: 2,
        opacity: 0.5,
      });
    } else {
      this.changeState(TrajectoryState.CreatingMissionArea);
      const rset = (region.rset as any).map(
        (e) => new L.LatLng(e.latitude, e.longitude),
      );
      this.regionPolygon = new L.Polygon(rset, {
        color: this.colors.getColor(this.currentStage),
        weight: 2,
        opacity: 0.5,
      });
    }
    this.regionPolygon.on('click', (e) => {
      if (
        this.currentState !== TrajectoryState.CreatingMissionArea &&
        this.currentState !== TrajectoryState.CreatingMissionAreaInFork &&
        this.currentState !== TrajectoryState.ClosingMissionArea &&
        this.currentState !== TrajectoryState.ClosingMissionAreaInFork
      ) {
        this.leafletmap.fireEvent('click', e);
      }
    });
    this.regionPolygon.bindTooltip(region?.name || 'Mission Area', {
      permanent: false,
      sticky: true,
      opacity: 0.9,
    });
    switch (this.currentStage) {
      case 'Departure': {
        this.polygonLayers.addLayer(this.regionPolygon);
        break;
      }
      case 'Mission': {
        this.polygonLayers.addLayer(this.regionPolygon);
        break;
      }
      case 'Arrival': {
        this.polygonLayers.addLayer(this.regionPolygon);
        break;
      }
    }
    // Strip circular/non-serializable references set by the directory service
    // (directoryRef → parent Directory → items → region closes the circle)
    // Also strip polygonLayer (Leaflet Polygon instance) which cannot be cloned
    // by structuredClone used in trajectoryUtils.toAutopilotCommand.
    const cleanRegion = { ...region } as any;
    delete cleanRegion.directoryRef;
    delete cleanRegion.leafletLayerRef;
    delete cleanRegion.polygonLayer;
    this.region = cleanRegion;
    this.ref.detectChanges();
  }

  /**
   * Show select region modal to select the region that will be added to the trajectory.
   * Passes the current trajectory's bounding box and last waypoint so the modal map
   * can center there and show the user where to place the next region.
   */
  async presentSelectRegionModal() {
    // Build trajectory context for the modal ─────────────────────────
    const bounds = L.latLngBounds([]);
    let lastPoint: L.LatLng | null = null;
    const allWaypoints: AutopilotCommand[] = [];

    const processCmd = (cmd: AutopilotCommand) => {
      if (cmd?.point?.latitude != null && cmd?.point?.longitude != null) {
        const ll = new L.LatLng(cmd.point.latitude, cmd.point.longitude);
        bounds.extend(ll);
        lastPoint = ll;
        allWaypoints.push(cmd);
      }
    };

    this.waypointsDeparture.forEach((branchMap) =>
      branchMap.forEach((_marker, cmd) => processCmd(cmd)),
    );
    this.waypointsMission.forEach((_marker, cmd) => processCmd(cmd));
    this.waypointsArrival.forEach((branchMap) =>
      branchMap.forEach((_marker, cmd) => processCmd(cmd)),
    );
    // ─────────────────────────────────────────────────────────────────

    const modal = await this.modalController.create({
      backdropDismiss: true,
      component: SelectRegionAreaComponent,
      cssClass: 'add-region-modal',
      componentProps: {
        lastWaypointLatLng: lastPoint,
        trajectoryBounds: bounds.isValid() ? bounds : null,
        trajectoryWaypoints: allWaypoints,
      },
    });

    modal.present();
    modal.onDidDismiss().then(({ data }) => {
      if (data) {
        this.drawRegionSelected(data);
      } else {
        this.toastService.presentToast(
          this.translateService.instant('FP.TOAST.NO_REGION'),
          'bottom',
          5000,
          '',
          'warning',
        );
        this.changeWaypointType(AutopilotCommandEnum.SetWaypoint);
        this.ref.detectChanges();
      }
    });
  }

  /**
   * Method in charge to remove the Mission Area in case the waypoint to be removed is in it. It will also will adapt the track if there are waypoints
   * after the mission area.
   * @param waypoint: waypoint to be removed.
   * @param neBeWaypoint: waypoint after or before the waypoint to be removed (depending if is Entry or Exit point).
   * @param waypointVector: vector of waypoints (Map<AutopilotCommand, L.Marker>) to remove the waypoint from.
   * @param trackVector: vector of tracks (Map<AutopilotCommand, L.Polyline>) to remove the track from.
   * @param regionsVector: vector of regions (Map<AutopilotCommand, L.Polygon>) to remove the region from.
   */
  removeWaypointsMissionArea(
    waypoint: AutopilotCommand,
    neBeWaypoint: AutopilotCommand,
    waypointAfArea: AutopilotCommand,
    waypointBefArea: AutopilotCommand,
    waypointVector: Map<AutopilotCommand, L.Marker>,
    trackVector: Map<AutopilotCommand, L.Polyline>,
    regionsVector: Map<AutopilotCommand, L.Polygon>,
  ) {
    this.markersLayer.removeLayer(waypointVector.get(neBeWaypoint));
    waypointVector.delete(neBeWaypoint);
    this.tracksLayer.removeLayer(trackVector.get(neBeWaypoint));
    trackVector.delete(neBeWaypoint);
    this.polygonLayers.removeLayer(regionsVector.get(waypoint));
    regionsVector.delete(neBeWaypoint);
    regionsVector.delete(waypoint);
    this.tracksLayer.removeLayer(trackVector.get(waypoint));
    trackVector.delete(waypoint);
    this.markersLayer.removeLayer(waypointVector.get(waypoint));
    waypointVector.delete(waypoint);
    this.trajectoryTree.removeWaypointFromTree(waypoint);
    this.trajectoryTree.removeWaypointFromTree(neBeWaypoint);
    //ADAPT Track
    if (waypointAfArea && !waypoint.forkLabel) {
      this.tracksLayer.removeLayer(trackVector.get(waypointAfArea));
      trackVector
        .get(waypointAfArea)
        .setLatLngs([
          new L.LatLng(
            waypointBefArea.point.latitude,
            waypointBefArea.point.longitude,
          ),
          trackVector.get(waypointAfArea).getLatLngs()[1] as L.LatLng,
        ]);
      this.tracksLayer.addLayer(trackVector.get(waypointAfArea));
    }
  }

  /**
   * ADAPT WHEN REMOVING MISSION AREA (NEED TO REMOVE ENTRY AND EXIT WAYPOINT)
      1. Check if is Entry or Exit point
      2. If Entry point, remove next waypoint(track included), Area and waypoint(track included) and adapt if exists the track of the new next waypoint.
      3. If Exit point remove previous waypoint(track included), Area and waypoint(track included) and adapt if exists the track of the new next waypoint.
   * @param waypoint: AutoPilotCommand to be removed.
   */
  removeMissionArea(waypoint: AutopilotCommand) {
    let nextWaypoint;
    let waypointAfterArea;
    let waypointBeforeArea;
    let previousWaypoint;
    if (waypoint.apCommand === 'MissionArea') {
      switch (waypoint.stageLabel) {
        case 'Departure': {
          waypointBeforeArea = Array.from(
            this.waypointsDeparture.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsDeparture.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) - 1
          ];
          waypointAfterArea = Array.from(
            this.waypointsDeparture.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsDeparture.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) + 2
          ];
          nextWaypoint = Array.from(
            this.waypointsDeparture.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsDeparture.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) + 1
          ];
          this.removeWaypointsMissionArea(
            waypoint,
            nextWaypoint,
            waypointAfterArea,
            waypointBeforeArea,
            this.waypointsDeparture.get(waypoint.branchLabel),
            this.waypointsTracksDeparture,
            this.regionsDeparture,
          );
          break;
        }
        case 'Mission': {
          waypointBeforeArea = Array.from(this.waypointsMission.keys())[
            Array.from(this.waypointsMission.keys()).indexOf(waypoint) - 1
          ];
          waypointAfterArea = Array.from(this.waypointsMission.keys())[
            Array.from(this.waypointsMission.keys()).indexOf(waypoint) + 2
          ];
          nextWaypoint = Array.from(this.waypointsMission.keys())[
            Array.from(this.waypointsMission.keys()).indexOf(waypoint) + 1
          ];
          this.removeWaypointsMissionArea(
            waypoint,
            nextWaypoint,
            waypointAfterArea,
            waypointBeforeArea,
            this.waypointsMission,
            this.waypointsTracksMission,
            this.regionsMission,
          );
          break;
        }
        case 'Arrival': {
          waypointBeforeArea = Array.from(
            this.waypointsArrival.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsArrival.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) - 1
          ];
          waypointAfterArea = Array.from(
            this.waypointsArrival.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsArrival.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) + 2
          ];
          nextWaypoint = Array.from(
            this.waypointsArrival.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsArrival.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) + 1
          ];
          this.removeWaypointsMissionArea(
            waypoint,
            nextWaypoint,
            waypointAfterArea,
            waypointBeforeArea,
            this.waypointsArrival.get(waypoint.branchLabel),
            this.waypointsTracksArrival,
            this.regionsArrival,
          );
          break;
        }
      }
    } else {
      switch (waypoint.stageLabel) {
        case 'Departure': {
          previousWaypoint = Array.from(
            this.waypointsDeparture.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsDeparture.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) - 1
          ];
          waypointBeforeArea = Array.from(
            this.waypointsDeparture.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsDeparture.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) - 2
          ];
          waypointAfterArea = Array.from(
            this.waypointsDeparture.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsDeparture.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) + 1
          ];
          this.removeWaypointsMissionArea(
            waypoint,
            previousWaypoint,
            waypointAfterArea,
            waypointBeforeArea,
            this.waypointsDeparture.get(waypoint.branchLabel),
            this.waypointsTracksDeparture,
            this.regionsDeparture,
          );
          break;
        }
        case 'Mission': {
          previousWaypoint = Array.from(this.waypointsMission.keys())[
            Array.from(this.waypointsMission.keys()).indexOf(waypoint) - 1
          ];
          waypointBeforeArea = Array.from(this.waypointsMission.keys())[
            Array.from(this.waypointsMission.keys()).indexOf(waypoint) - 2
          ];
          waypointAfterArea = Array.from(this.waypointsMission.keys())[
            Array.from(this.waypointsMission.keys()).indexOf(waypoint) + 1
          ];
          this.removeWaypointsMissionArea(
            waypoint,
            previousWaypoint,
            waypointAfterArea,
            waypointBeforeArea,
            this.waypointsMission,
            this.waypointsTracksMission,
            this.regionsMission,
          );
          break;
        }
        case 'Arrival': {
          previousWaypoint = Array.from(
            this.waypointsArrival.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsArrival.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) - 1
          ];
          waypointBeforeArea = Array.from(
            this.waypointsArrival.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsArrival.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) - 2
          ];
          waypointAfterArea = Array.from(
            this.waypointsArrival.get(waypoint.branchLabel).keys(),
          )[
            Array.from(
              this.waypointsArrival.get(waypoint.branchLabel).keys(),
            ).indexOf(waypoint) + 1
          ];
          this.removeWaypointsMissionArea(
            waypoint,
            previousWaypoint,
            waypointAfterArea,
            waypointBeforeArea,
            this.waypointsArrival.get(waypoint.branchLabel),
            this.waypointsTracksArrival,
            this.regionsArrival,
          );
          break;
        }
      }
    }
    if (waypoint.forkLabel) {
      this.currentSelection = waypoint.forkLabel;
      this.removeCurrentSelection(waypoint);
    }
    this.ref.detectChanges();
  }

  /**
   * Remove Mission Area Confirmation
   */
  async presentRemovalMissionAreaConfirmation(waypoint: AutopilotCommand) {
    const removeMissionArea = () => {
      this.removeMissionArea(waypoint);
    };

    const alert = await this.alertController.create({
      header: this.translateService.instant('FP.TOAST.CONFIRM_TITLE'),
      message: this.translateService.instant(
        'FP.TOAST.CONFIRM_REMOVE_AREA_MESSAGE',
      ),
      cssClass: 'custom-alert',
      buttons: [
        {
          text: this.translateService.instant('COMMON.BUTTONS.CANCEL'),
          role: 'cancel',
          handler: () => null,
        },
        {
          text: this.translateService.instant('COMMON.BUTTONS.SURE'),
          handler: () => {
            removeMissionArea();
          },
        },
      ],
    });

    await alert.present();
  }

  /**
   * Present Remove Fork Confirmation
   */
  async presentRemovalForkConfirmation(waypoint: AutopilotCommand) {
    const removeFork = () => {
      this.currentSelection = waypoint.forkLabel;
      this.removeCurrentSelection(waypoint);
    };

    const alert = await this.alertController.create({
      header: this.translateService.instant('FP.TOAST.CONFIRM_TITLE'),
      message: this.translateService.instant(
        'FP.TOAST.CONFIRM_REMOVE_FORK_MESSAGE',
      ),
      cssClass: 'custom-alert',
      buttons: [
        {
          text: this.translateService.instant('COMMON.BUTTONS.CANCEL'),
          role: 'cancel',
          handler: () => null,
        },
        {
          text: this.translateService.instant('COMMON.BUTTONS.SURE'),
          handler: () => {
            removeFork();
          },
        },
      ],
    });

    await alert.present();
  }

  /**
   * Method called from the button in the interface to undo the last waypoint added. It will remove the last waypoint from the vectors and the tree.
   * And it will also remove the branches in the tree if the waypoint was the last remaining in the branch.
   */
  undoLastWaypoint(): void {
    this.currentSelection = null;
    this.attenuateTrajectory();
    this.ref.detectChanges();
    if (
      this.waypointsDeparture.size === 0 &&
      this.waypointsMission.size === 0 &&
      this.waypointsArrival.size === 0
    ) {
      return;
    }

    switch (this.currentStage) {
      case 'Departure': {
        const depBranch = this.waypointsDeparture.get(this.currentBranch);
        if (!depBranch) break;
        this.undoRemoveWaypoint(depBranch, this.waypointsTracksDeparture);
        break;
      }
      case 'Mission': {
        this.undoRemoveWaypoint(
          this.waypointsMission,
          this.waypointsTracksMission,
        );
        break;
      }
      case 'Arrival': {
        const arrBranch = this.waypointsArrival.get(this.currentBranch);
        if (!arrBranch) break;
        this.undoRemoveWaypoint(arrBranch, this.waypointsTracksArrival);
        break;
      }
    }

    // Check if the there is any waypoint left in the current fork/branch and if not remove the branch from the tree and switch to the next available.
    if (this.currentStage === 'Arrival') {
      if (
        this.trajectoryTree.tree.searchByName(this.currentBranch)?.data.items
          .length === 0
      ) {
        this.trajectoryTree.tree.delete(
          this.trajectoryTree.tree.searchByName(this.currentBranch)?.data,
        );
        this.waypointsArrival.delete(this.currentBranch);
        this.updateTree();
        this.currentBranch = null;
        this.trajectoryTree.tree.obtainArray().forEach((item: PlanningItem) => {
          if (item.name && item.name.includes('Track Arrival')) {
            if (!this.currentBranch) this.currentBranch = item.name;
          }
        });
      }

      let itemRemove;
      this.trajectoryTree.tree
        .searchByName(this.currentBranch)
        ?.data.items.forEach((item: PlanningItem) => {
          if (item.name && item.name.includes('Fork')) {
            if (item.items.length === 0) {
              itemRemove = item;
            }
          }
        });
      if (itemRemove) {
        this.trajectoryTree.tree
          .searchByName(this.currentBranch)
          ?.data.items.splice(
            this.trajectoryTree.tree
              .searchByName(this.currentBranch)
              ?.data.items.indexOf(itemRemove),
            1,
          );
        this.updateTree();
        this.changeState(
          Array.from(this.waypointsArrival.get(this.currentBranch).keys()).pop()
            .stateTrajectory,
        );
      }
    }

    // Check if the there is any waypoint left in the current fork/branch and if not remove the branch from the tree and switch to the next available.
    if (this.currentStage === 'Departure') {
      if (
        this.trajectoryTree.tree.searchByName(this.currentBranch)?.data.items
          .length === 0
      ) {
        this.trajectoryTree.tree.delete(
          this.trajectoryTree.tree.searchByName(this.currentBranch)?.data,
        );
        this.waypointsDeparture.delete(this.currentBranch);
        this.updateTree();
        this.currentBranch = null;
        this.trajectoryTree.tree.obtainArray().forEach((item: PlanningItem) => {
          if (item.name && item.name.includes('Track Initial')) {
            if (!this.currentBranch) this.currentBranch = item.name;
          }
        });
      }
      let itemRemove;
      this.trajectoryTree.tree
        .searchByName(this.currentBranch)
        ?.data.items.forEach((item: PlanningItem) => {
          if (item.name && item.name.includes('Fork')) {
            if (item.items.length === 0) {
              itemRemove = item;
            }
          }
        });
      if (itemRemove) {
        this.trajectoryTree.tree
          .searchByName(this.currentBranch)
          ?.data.items.splice(
            this.trajectoryTree.tree
              .searchByName(this.currentBranch)
              ?.data.items.indexOf(itemRemove),
            1,
          );
        this.updateTree();
        this.changeState(
          Array.from(
            this.waypointsDeparture.get(this.currentBranch).keys(),
          ).pop().stateTrajectory,
        );
      }
    }

    // Check if the there is any waypoint left in the current fork/branch and if not remove the branch from the tree and switch to the next available.
    if (this.currentStage === 'Mission') {
      let itemRemove;
      this.trajectoryTree.tree
        .searchByName(this.currentStage)
        ?.data.items.forEach((item: PlanningItem) => {
          if (item.name && item.name.includes('Fork')) {
            if (item.items.length === 0) {
              itemRemove = item;
            }
          }
        });
      if (itemRemove) {
        this.trajectoryTree.tree
          .searchByName(this.currentStage)
          ?.data.items.splice(
            this.trajectoryTree.tree
              .searchByName(this.currentStage)
              ?.data.items.indexOf(itemRemove),
            1,
          );
        this.updateTree();
        this.changeState(
          this.getLastKey(this.waypointsMission).stateTrajectory,
        );
      }
    }

    this.refreshStageJoints();
  }

  /**
   * Method to remove the last waypoint added in the current stage. We will remove the last waypoint in the vectors and change the stage and state
   * to the following waypoint to maintain the logic of the trajectory creation. It also will remove the track associated to the waypoint if there is any.
   * @param waypointVector: vector of waypoints (Map<AutopilotCommand, L.Marker>) to remove the last waypoint from.
   * @param trackVector: vector of tracks (Map<AutopilotCommand, L.Polyline>) to remove the last track from.
   */
  undoRemoveWaypoint(
    waypointVector: Map<AutopilotCommand, L.Marker>,
    trackVector: Map<AutopilotCommand, L.Polyline>,
  ) {
    // If the waypoint to remove is the first of an Initial or Arrival track we will not remove any track (none to remove).
    if (
      trackVector.size > 0 &&
      this.getLastKey(waypointVector).stateTrajectory !==
        TrajectoryState.CreatingInitialTrack &&
      this.getLastKey(waypointVector).stateTrajectory !==
        TrajectoryState.CreatingArrivalTrack
    ) {
      this.tracksLayer.removeLayer(Array.from(trackVector.values()).pop());
      trackVector.delete(this.getLastKey(trackVector));
    }

    // If the waypoint vector has any waypoint left we will remove the next keeping in mind the main logic.
    if (waypointVector && waypointVector.size > 0) {
      if (this.getLastKey(waypointVector).label === 'LastWaypoint') {
        this.lastWaypoint = this.getLastKey(waypointVector);
        if (this.lastWaypoint.apCommand !== 'JoinRoute') {
          //Elimino i agafo laltre
          waypointVector.delete(this.getLastKey(waypointVector));
        }
      }

      if (
        this.getLastKey(waypointVector).apCommand === 'JoinRoute' &&
        this.getLastKey(waypointVector).stateTrajectory ===
          TrajectoryState.CreatingForkTrack
      ) {
        this.currentJoin = this.getLastKey(waypointVector);
      }
      this.trajectoryTree.removeWaypointFromTree(
        this.getLastKey(waypointVector),
      );

      this.markersLayer.removeLayer(Array.from(waypointVector.values()).pop());
      this.changeStage(this.getLastKey(waypointVector).stageLabel);
      if (
        this.getLastKey(waypointVector).apCommand ===
        AutopilotCommandEnum.JoinRoute
      ) {
        waypointVector.delete(this.getLastKey(waypointVector));
        if (
          this.getLastKey(waypointVector).stateTrajectory ===
          TrajectoryState.ClosingMissionAreaInFork
        ) {
          this.changeState(TrajectoryState.InForkTrack);
        } else {
          this.changeState(
            this.getLastKey(waypointVector).stateTrajectory,
          );
        }
      } else {
        this.changeState(
          this.getLastKey(waypointVector).stateTrajectory,
        );
        waypointVector.delete(this.getLastKey(waypointVector));
      }
    }
  }

  /**
   * Method to apply the main logic and switch automatically to the next state depending on the waypoint added into the trajectory.
   * @param point: coordinates of the click on the map to create the waypoint.
   */
  applyWaypointLogic(point: L.LatLng) {
    if (
      this.currentStage === 'Departure' &&
      this.currentWaypointType === AutopilotCommandEnum.Takeoff
    ) {
      this.createWaypoint(point);
      if (this.isDevel()) {
        this.changeState(TrajectoryState.InInitialTrack);
      } else {
        this.changeStageDirectly('Mission');
      }
    } else if (
      this.currentStage === 'Arrival' &&
      this.currentWaypointType === AutopilotCommandEnum.Land
    ) {
      this.createWaypoint(point);
      this.changeState(TrajectoryState.LandingAdded);
    } else if (this.currentWaypointType === AutopilotCommandEnum.ForkRoute) {
      this.createWaypoint(point);
      this.trajectoryTree.startForkEntry(
        this.currentStage,
        this.currentState,
        null,
        this.currentBranch,
      );
      this.changeState(TrajectoryState.InForkTrack);
      this.toastService.presentToast(
        this.translateService.instant('FP.TOAST.ADD_JR'),
        'bottom',
        5000,
        '',
        'zomp',
      );
    } else if (this.currentWaypointType === AutopilotCommandEnum.JoinRoute) {
      // CHECK IF FORK HAS AT LEAST ONE WAYPOINT
      this.isFirstFork = false;
      if (this.currentStage === 'Mission') {
        this.changeState(TrajectoryState.InMission);
      } else if (this.currentStage === 'Arrival') {
        this.changeState(TrajectoryState.InArrivalTrack);
      } else if (this.currentStage === 'Departure') {
        this.changeState(TrajectoryState.InInitialTrack);
      }
      this.changeWaypointType(AutopilotCommandEnum['JoinRoute']);
      this.createWaypoint(point);
      this.changeWaypointType(AutopilotCommandEnum['SetWaypoint']);
      this.ref.detectChanges();
    } else if (this.currentState === TrajectoryState.CreatingMissionArea) {
      const rset = this.regionPolygon.getLatLngs()[0] as Array<L.LatLng>;
      if (this.trajectoryUtils.isInRegion(point, rset)) {
        this.createWaypoint(point);
        this.changeState(TrajectoryState.ClosingMissionArea);
      } else {
        this.toastService.presentToast(
          this.translateService.instant('FP.TOAST.WAYPOINT_OUT'),
          'bottom',
          5000,
          '',
          'zomp',
        );
      }
    } else if (this.currentState === TrajectoryState.ClosingMissionArea) {
      const rset = this.regionPolygon.getLatLngs()[0] as Array<L.LatLng>;
      if (this.trajectoryUtils.isInRegion(point, rset)) {
        this.createWaypoint(point);

        this.changeStageDirectly(this.currentStage);
        this.region = null;
        this.regionPolygon = null;
      } else {
        this.toastService.presentToast(
          this.translateService.instant('FP.TOAST.WAYPOINT_OUT'),
          'bottom',
          5000,
          '',
          'zomp',
        );
      }
    } else if (
      this.currentState === TrajectoryState.CreatingMissionAreaInFork
    ) {
      const rset = this.regionPolygon.getLatLngs()[0] as Array<L.LatLng>;
      if (this.trajectoryUtils.isInRegion(point, rset)) {
        this.createWaypoint(point);
        this.changeState(TrajectoryState.ClosingMissionAreaInFork);
      } else {
        this.toastService.presentToast(
          this.translateService.instant('FP.TOAST.WAYPOINT_OUT'),
          'bottom',
          5000,
          '',
          'zomp',
        );
      }
    } else if (this.currentState === TrajectoryState.ClosingMissionAreaInFork) {
      const rset = this.regionPolygon.getLatLngs()[0] as Array<L.LatLng>;
      if (this.trajectoryUtils.isInRegion(point, rset)) {
        this.createWaypoint(point);

        if (this.isFirstFork) {
          this.changeState(TrajectoryState.InForkTrack);
        } else {
          this.changeState(TrajectoryState.CreatingForkTrack);
        }
        this.region = null;
        this.regionPolygon = null;
      } else {
        this.toastService.presentToast(
          this.translateService.instant('FP.TOAST.WAYPOINT_OUT'),
          'bottom',
          5000,
          '',
          'zomp',
        );
      }
    } else {
      this.createWaypoint(point);
    }
    this.ref.detectChanges();
  }

  /**
   * Method to adapt all booleans and variables to the desired state to comply with the main logic of the trajectory creation.
   * @param nextState: next desired state to change to.
   */
  changeState(nextState: TrajectoryState) {
    switch (nextState) {
      case TrajectoryState.CreatingInitialTrack: {
        this.currentState = TrajectoryState.CreatingInitialTrack;
        this.changeWaypointType(AutopilotCommandEnum['Takeoff']);
        this.isTakeoffEnabled = true;
        this.isLandEnabled = false;
        this.isForkEnabled = false;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = false;
        this.isAddingFork = false;
        this.isFirstFork = false;
        this.isRegionEnabled = false;
        break;
      }
      case TrajectoryState.InInitialTrack: {
        this.currentState = TrajectoryState.InInitialTrack;
        this.changeWaypointType(AutopilotCommandEnum['SetWaypoint']);
        this.isTakeoffEnabled = false;
        this.isLandEnabled = false;
        this.isForkEnabled = true;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = true;
        this.isRegionEnabled = true;
        this.isAddingFork = false;
        this.isFirstFork = false;
        break;
      }
      case TrajectoryState.CreatingForkTrack: {
        this.currentState = TrajectoryState.CreatingForkTrack;
        this.changeWaypointType(AutopilotCommandEnum['SetWaypoint']);
        this.isForkEnabled = false;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = true;
        this.isRegionEnabled = true;
        this.isAddingFork = true;
        this.isFirstFork = false;
        break;
      }
      case TrajectoryState.InForkTrack: {
        this.currentState = TrajectoryState.InForkTrack;
        this.changeWaypointType(AutopilotCommandEnum['SetWaypoint']);
        this.isForkEnabled = false;
        this.isJoinEnabled = true;
        this.isWaypointEnabled = true;
        this.isRegionEnabled = true;
        this.isLandEnabled = false;
        this.isTakeoffEnabled = false;
        this.isAddingFork = false;
        this.isFirstFork = true;
        break;
      }
      case TrajectoryState.InMission: {
        this.currentState = TrajectoryState.InMission;
        this.changeWaypointType(AutopilotCommandEnum['SetWaypoint']);
        this.isTakeoffEnabled = false;
        this.isLandEnabled = false;
        if (!this.isDevel()) {
          this.isLandEnabled = true;
        }
        this.isForkEnabled = true;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = true;
        this.isRegionEnabled = true;
        this.isAddingFork = false;
        this.isFirstFork = false;
        break;
      }
      case TrajectoryState.CreatingMissionArea: {
        this.currentState = TrajectoryState.CreatingMissionArea;
        this.isTakeoffEnabled = false;
        this.isLandEnabled = false;
        this.isForkEnabled = false;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = false;
        this.isRegionEnabled = false;
        this.isAddingFork = false;
        this.isFirstFork = false;
        break;
      }
      case TrajectoryState.ClosingMissionArea: {
        this.currentState = TrajectoryState.ClosingMissionArea;
        this.changeWaypointType(AutopilotCommandEnum['SetWaypoint']);
        this.isTakeoffEnabled = false;
        this.isLandEnabled = false;
        this.isForkEnabled = false;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = false;
        this.isRegionEnabled = false;
        this.isAddingFork = false;
        this.isFirstFork = false;
        break;
      }
      case TrajectoryState.CreatingMissionAreaInFork: {
        this.currentState = TrajectoryState.CreatingMissionAreaInFork;
        this.isTakeoffEnabled = false;
        this.isLandEnabled = false;
        this.isForkEnabled = false;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = false;
        this.isRegionEnabled = false;
        break;
      }
      case TrajectoryState.ClosingMissionAreaInFork: {
        this.currentState = TrajectoryState.ClosingMissionAreaInFork;
        this.changeWaypointType(AutopilotCommandEnum['SetWaypoint']);
        this.isTakeoffEnabled = false;
        this.isLandEnabled = false;
        this.isForkEnabled = false;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = false;
        this.isRegionEnabled = false;
        break;
      }
      case TrajectoryState.CreatingArrivalTrack: {
        this.currentState = TrajectoryState.CreatingArrivalTrack;
        if (this.currentWaypointType !== AutopilotCommandEnum.Land)
          this.changeWaypointType(AutopilotCommandEnum['SetWaypoint']);
        this.isTakeoffEnabled = false;
        this.isLandEnabled = true;
        this.isForkEnabled = true;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = true;
        this.isRegionEnabled = true;
        this.isAddingFork = false;
        this.isFirstFork = false;
        break;
      }
      case TrajectoryState.InArrivalTrack: {
        this.currentState = TrajectoryState.InArrivalTrack;
        this.changeWaypointType(AutopilotCommandEnum['SetWaypoint']);
        this.isTakeoffEnabled = false;
        this.isLandEnabled = true;
        this.isForkEnabled = true;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = true;
        this.isRegionEnabled = true;
        this.isAddingFork = false;
        this.isFirstFork = false;
        break;
      }
      case TrajectoryState.LandingAdded: {
        this.currentState = TrajectoryState.LandingAdded;
        this.isTakeoffEnabled = false;
        this.isLandEnabled = false;
        this.isForkEnabled = false;
        this.isJoinEnabled = false;
        this.isWaypointEnabled = false;
        this.isRegionEnabled = false;
        this.isAddingFork = false;
        this.isFirstFork = false;
        break;
      }
      default: {
        break;
      }
    }
    this.ref.detectChanges();
  }

  //------------------------------------------------------------------------------------------------
  //#region --> To manage the search of near infrastructures

  /**
   * Toggles the visibility of the search dialog and updates center values
   */
  manageSearchNearInfrasDialog() {
    if (this.activeFab === 'searchInfras') {
      setTimeout(() => {
        if (this.checkNearInfrasComponent) {
          this.updateCenterValues();
        } else {
          console.error('checkNearInfrasComponent is not initialized');
        }
      }, 0);
    }
  }

  manageSearchUrbanAreasDialog() {
    if (this.activeFab === 'searchUrbanArea') {
      setTimeout(() => {
        if (this.checkNearUrbanAreasComponent) {
          this.updateCenterValues();
        } else {
          console.error('checkNearUrbanAreasComponent is not initialized');
        }
      }, 0);
    }
  }

  manageSearchNearTracksDialog() {
    if (this.activeFab === 'searchNearTracks') {
      setTimeout(() => {
        if (this.checkNearTracksComponent) {
          this.updateCenterValues();
        } else {
          console.error('checkNearTracksComponent is not initialized');
        }
      }, 0);
    }
  }

  /**
   * Updates the center values and marker position
   */
  updateCenterValues() {
    const center = this.leafletmap.getCenter();

    if (this.activeFab === 'searchInfras' && this.checkNearInfrasComponent) {
      this.checkNearInfrasComponent.updatePosition(center.lat, center.lng);
    }

    if (
      this.activeFab === 'searchUrbanArea' &&
      this.checkNearUrbanAreasComponent
    ) {
      this.checkNearUrbanAreasComponent.updatePosition(center.lat, center.lng);
    }

    if (
      this.activeFab === 'searchNearTracks' &&
      this.checkNearTracksComponent
    ) {
      this.checkNearTracksComponent.updatePosition(center.lat, center.lng);
    }

    this.updateSearchRadiusCircle(center);
    this.ref.detectChanges();
  }

  /**
   * Updates the search radius circle
   */
  updateSearchRadiusCircle(center: L.LatLng) {
    // Determine which component's radius to use based on the active FAB
    let radius: number;

    if (this.activeFab === 'searchInfras' && this.checkNearInfrasComponent) {
      radius = this.checkNearInfrasComponent.radius;
    } else if (
      this.activeFab === 'searchUrbanArea' &&
      this.checkNearUrbanAreasComponent
    ) {
      radius = this.checkNearUrbanAreasComponent.radius;
    } else if (
      this.activeFab === 'searchNearTracks' &&
      this.checkNearTracksComponent
    ) {
      radius = this.checkNearTracksComponent.radius;
    } else {
      // Default fallback - could be either component's radius or a default value
      radius =
        this.checkNearInfrasComponent?.radius ||
        this.checkNearUrbanAreasComponent?.radius ||
        40000;
    }

    if (this.searchRadiusCircle) {
      this.searchRadiusCircle.setLatLng(center).setRadius(radius); // Update the center and radius
    } else {
      this.searchRadiusCircle = L.circle(center, {
        radius: radius,
        color: '#e1ff00',
        fillColor: 'none',
        weight: 2,
        dashArray: '5, 10',
        interactive: false,
        bubblingMouseEvents: false,
      }).addTo(this.leafletmap);
    }
  }

  /**
   * When the radius is changed, updates the search radius circle
   */
  onRadiusChange() {
    this.updateSearchRadiusCircle(this.leafletmap.getCenter());
  }

  /**
   * Update and draws near infrastructures
   * @param infrastructures Array of infrastructures to draw
   */
  updateNearInfras(infrastructures: Infrastructure[]) {
    this.clearInfrastructures();
    this.infrastructureMarkers = this.leafletMapCmp.drawInfrastructures(
      infrastructures,
      false,
      this.leafletmap,
      true,
    );
  }

  /**
   * Update and draws near urban areas
   * @param urbanAreas Array of infrastructures to draw
   */
  updateNearUrbanAreas(urbanAreas: UrbanAreasBasicInfo[]) {
    this.clearUrbanAreas();
    this.leafletMapCmp.drawUrbanAreas(urbanAreas, this.urbanAreasLayerGroup, this.urbanAreasRegistryLayerGroup, false);
  }

  updateNearTrackSegments(tracksSegment: TrackNetworkSegment[]) {
    this.clearNearTracks();
    this.infrastructureMarkers = this.leafletMapCmp.drawTrackSegments(
      tracksSegment,
      this.trackNetworkLayerGroup,
      this.leafletmap,
      false,
    );
  }

  async onInfrastructuresLoading(isLoading: boolean) {
    if (isLoading) {
      if (this.loadingEl) return; // already showing
      this.loadingEl = await this.loadingControl.create({
        message: this.translateService.instant('COMMON.LOADING.TAKE_A_WHILE'),
        spinner: 'dots',
        backdropDismiss: false,
      });
      await this.loadingEl.present();
    } else {
      if (!this.loadingEl) return;
      await this.loadingEl.dismiss();
      this.loadingEl = undefined;
    }
  }

  /**
   * Clears all infrastructure markers from the map and center marker when closing the dialog
   * @param closeDialog Optional: whether to remove the center marker as well
   */
  clearInfrastructures(closeDialog?: boolean) {
    this.infrastructureMarkers.forEach((marker) => marker.remove());
    this.infrastructureMarkers = [];

    // Clear all infrastructure layer groups
    this.structuresLayerGroup.clearLayers();
    this.protectionLowLayerGroup.clearLayers();
    this.protectionHighLayerGroup.clearLayers();
    this.ctrAtzLayerGroup.clearLayers();
    this.naturalParksLayerGroup.clearLayers();
    this.harboursLayerGroup.clearLayers();
    this.innerZonesLayerGroup.clearLayers();
    this.runwayLayerGroup.clearLayers();
    this.otherLayerGroup.clearLayers();

    // Remove search radius circle
    if (closeDialog && this.searchRadiusCircle) {
      this.searchRadiusCircle.remove();
      this.searchRadiusCircle = null;
    }
  }

  /**
   * Clears all urban areas from the map and center marker when closing the dialog
   * @param closeDialog Optional: whether to remove the center marker as well
   */
  clearUrbanAreas(closeDialog?: boolean) {
    this.urbanAreasLayerGroup?.clearLayers();
    this.urbanAreasRegistryLayerGroup?.clearLayers();

    if (closeDialog) {
      // Remove both groups from the map and the panel
      this.leafletmap?.removeLayer(this.urbanAreasLayerGroup);
      this.leafletmap?.removeLayer(this.urbanAreasRegistryLayerGroup);
      this.leafletMapCmp?.unregisterLayer('near-urban-areas-population');
      this.leafletMapCmp?.unregisterLayer('near-urban-areas-registry');

      if (this.searchRadiusCircle) {
        this.searchRadiusCircle.remove();
        this.searchRadiusCircle = null;
      }
    }
  }

  /**
   * Clears all tracks from the map and center marker when closing the dialog
   * @param closeDialog Optional: whether to remove the center marker as well
   */
  clearNearTracks(closeDialog?: boolean) {
    this.trackNetworkLayerGroup.clearLayers();
    this.infrastructureMarkers.forEach((marker) => marker.remove());
    this.infrastructureMarkers = [];

    if (closeDialog && this.searchRadiusCircle) {
      this.searchRadiusCircle.remove();
      this.searchRadiusCircle = null;
    }
  }

  //#endregion

  //#region --> Manage Visualization

  /**
   * Toggle the visualization of the trajectory card
   */
  toggleTrajectoryCard() {
    this.showTrajectoryCard = !this.showTrajectoryCard;
  }

  /**
   * Method to obtain the information of a waypoint to be shown in the popup
   * @param waypoint: AutopilotCommand containing all the information of the waypoint
   * @param translate: TranslateService to obtain the translations
   * @returns string with the information to be shown in the popup.
   */
  obtainMarkerPopup(
    waypoint: AutopilotCommand,
    translate: TranslateService,
  ): string {
    return `
        <div>
        <strong>${translate.instant('FP.INFO.STAGE')}: ${waypoint.stageLabel}</strong><br>
        <strong>${translate.instant('FP.INFO.LABEL')}: ${waypoint.label}</strong><br>
        <strong>${translate.instant('FP.INFO.TYPE')}: ${waypoint.apCommand}</strong><br>
        <strong>${translate.instant('FP.INFO.SPEED')}: ${waypoint.speed} ${AutopilotSpeedUnitsStringEnum.mps.valueOf()}</strong><br>
        <strong>${translate.instant('FP.INFO.ALTITUDE')}: ${waypoint.point.altitude} ${AutopilotAltitudeUnitsStringEnum.meters.valueOf()}</strong><br>
        </div>
        `;
  }

  /**
   * Method to adapt all the tracks involved after or during a waypoint dragging.
   * @param waypoint: AutopilotCommand being dragged.
   * @param waypointVector: stage waypoint vector where the waypoint is located.
   * @param trackVector: stage track vector where the waypoint's track is located.
   * @param regionsVector: in case the waypoint is in a region, the region vector where the region is located.
   * @param point: new point where the waypoint is being dragged.
   * @param layerTracks: layer group containing all the tracks of the specific stage.
   * @param leftPanelTree: left panel tree component to update the coordinates of the waypoint being dragged.
   * @param lastWaypoint: specific waypoint created for JoinRoute and forks cases.
   */
  adaptTracksAfterWaypointMoved(
    waypoint: AutopilotCommand,
    waypointVector: Map<AutopilotCommand, L.Marker>,
    trackVector: Map<AutopilotCommand, L.Polyline>,
    regionsVector: Map<AutopilotCommand, L.Polygon>,
    point: L.LatLng,
    layerTracks: L.LayerGroup<L.Polyline>,
    leftPanelTree: LeftPanelTreeComponent,
    lastWaypoint: AutopilotCommand,
  ) {
    if (waypoint.apCommand === 'JoinRoute') {
      waypointVector.forEach((value: L.Marker, key: AutopilotCommand) => {
        if (key.apCommand === 'JoinRoute' && trackVector.get(key)) {
          layerTracks.removeLayer(trackVector.get(key));
          trackVector
            .get(key)
            .setLatLngs([
              trackVector.get(key).getLatLngs()[0] as L.LatLng,
              point,
            ]);
          layerTracks.addLayer(trackVector.get(key));
          waypointVector.get(key).setLatLng(point);

          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).indexOf(key)
          ].point.latitude = point.lat;
          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).indexOf(key)
          ].point.longitude = point.lng;
          if (
            Array.from(trackVector.keys())[
              Array.from(trackVector.keys()).indexOf(key)
            ]
          ) {
            Array.from(trackVector.keys())[
              Array.from(trackVector.keys()).indexOf(key)
            ].point.latitude = point.lat;
            Array.from(trackVector.keys())[
              Array.from(trackVector.keys()).indexOf(key)
            ].point.longitude = point.lng;
          }
        } else if (key.apCommand === 'JoinRoute' && !trackVector.get(key)) {
          waypointVector.get(key).setLatLng(point);
          leftPanelTree.updateCoordinatesAfterDrag(
            point,
            Array.from(waypointVector.keys())[
              Array.from(waypointVector.keys()).indexOf(key)
            ],
          );

          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).indexOf(key)
          ].point.latitude = point.lat;
          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).indexOf(key)
          ].point.longitude = point.lng;
        }
      });
      waypointVector.get(waypoint).setLatLng(point);
      leftPanelTree.updateCoordinatesAfterDrag(
        point,
        Array.from(waypointVector.keys())[
          Array.from(waypointVector.keys()).indexOf(waypoint)
        ],
      );
    }

    if (waypoint.apCommand !== 'JoinRoute') {
      if (
        trackVector.get(waypoint) &&
        waypoint.stateTrajectory !== TrajectoryState.CreatingMissionArea &&
        waypoint.stateTrajectory !== TrajectoryState.ClosingMissionArea
      ) {
        layerTracks.removeLayer(trackVector.get(waypoint));
        trackVector
          .get(waypoint)
          .setLatLngs([
            trackVector.get(waypoint).getLatLngs()[0] as L.LatLng,
            point,
          ]);
        layerTracks.addLayer(trackVector.get(waypoint));
        waypointVector.get(waypoint).setLatLng(point);

        leftPanelTree.updateCoordinatesAfterDrag(
          point,
          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).indexOf(waypoint)
          ],
        );
        if (lastWaypoint === waypoint) {
          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).length - 1
          ].point.latitude = point.lat;
          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).length - 1
          ].point.longitude = point.lng;
        }

        Array.from(waypointVector.keys())[
          Array.from(waypointVector.keys()).indexOf(waypoint)
        ].point.latitude = point.lat;
        Array.from(waypointVector.keys())[
          Array.from(waypointVector.keys()).indexOf(waypoint)
        ].point.longitude = point.lng;
        if (
          Array.from(trackVector.keys())[
            Array.from(trackVector.keys()).indexOf(waypoint)
          ]
        ) {
          Array.from(trackVector.keys())[
            Array.from(trackVector.keys()).indexOf(waypoint)
          ].point.latitude = point.lat;
          Array.from(trackVector.keys())[
            Array.from(trackVector.keys()).indexOf(waypoint)
          ].point.longitude = point.lng;
        }
      } else if (
        trackVector.get(waypoint) &&
        (waypoint.stateTrajectory === TrajectoryState.CreatingMissionArea ||
          waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea)
      ) {
        const rset = regionsVector
          .get(waypoint)
          .getLatLngs()[0] as Array<L.LatLng>;
        if (this.trajectoryUtils.isInRegion(point, rset)) {
          layerTracks.removeLayer(trackVector.get(waypoint));
          trackVector
            .get(waypoint)
            .setLatLngs([
              trackVector.get(waypoint).getLatLngs()[0] as L.LatLng,
              point,
            ]);
          layerTracks.addLayer(trackVector.get(waypoint));
          waypointVector.get(waypoint).setLatLng(point);

          leftPanelTree.updateCoordinatesAfterDrag(
            point,
            Array.from(waypointVector.keys())[
              Array.from(waypointVector.keys()).indexOf(waypoint)
            ],
          );
          if (lastWaypoint === waypoint) {
            Array.from(waypointVector.keys())[
              Array.from(waypointVector.keys()).length - 1
            ].point.latitude = point.lat;
            Array.from(waypointVector.keys())[
              Array.from(waypointVector.keys()).length - 1
            ].point.longitude = point.lng;
          }

          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).indexOf(waypoint)
          ].point.latitude = point.lat;
          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).indexOf(waypoint)
          ].point.longitude = point.lng;
          if (
            Array.from(trackVector.keys())[
              Array.from(trackVector.keys()).indexOf(waypoint)
            ]
          ) {
            Array.from(trackVector.keys())[
              Array.from(trackVector.keys()).indexOf(waypoint)
            ].point.latitude = point.lat;
            Array.from(trackVector.keys())[
              Array.from(trackVector.keys()).indexOf(waypoint)
            ].point.longitude = point.lng;
          }
        }
      }

      if (waypoint.apCommand === 'Takeoff' || !trackVector.get(waypoint)) {
        waypointVector.get(waypoint).setLatLng(point);
        leftPanelTree.updateCoordinatesAfterDrag(
          point,
          Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).indexOf(waypoint)
          ],
        );

        Array.from(waypointVector.keys())[
          Array.from(waypointVector.keys()).indexOf(waypoint)
        ].point.latitude = point.lat;
        Array.from(waypointVector.keys())[
          Array.from(waypointVector.keys()).indexOf(waypoint)
        ].point.longitude = point.lng;
      }
    }

    if (waypoint.apCommand === 'ForkRoute') {
      if (waypoint.stageLabel !== 'Mission') {
        leftPanelTree.tree
          .searchByName(waypoint.branchLabel)
          .data.items.forEach((item: PlanningItem) => {
            if (item.name && item.name.includes('Fork')) {
              let nextWaypoint = item.items[0];
              if (nextWaypoint) {
                layerTracks.removeLayer(trackVector.get(nextWaypoint));
                trackVector
                  .get(nextWaypoint)
                  .setLatLngs([
                    point,
                    new L.LatLng(
                      nextWaypoint.point.latitude,
                      nextWaypoint.point.longitude,
                    ),
                  ]);
                layerTracks.addLayer(trackVector.get(nextWaypoint));
              }
            }
          });
      } else {
        leftPanelTree.tree
          .searchByName(waypoint.stageLabel)
          .data.items.forEach((item: PlanningItem) => {
            if (item.name && item.name.includes('Fork')) {
              let nextWaypoint = item.items[0];
              if (nextWaypoint) {
                layerTracks.removeLayer(trackVector.get(nextWaypoint));
                trackVector
                  .get(nextWaypoint)
                  .setLatLngs([
                    point,
                    new L.LatLng(
                      nextWaypoint.point.latitude,
                      nextWaypoint.point.longitude,
                    ),
                  ]);
                layerTracks.addLayer(trackVector.get(nextWaypoint));
              }
            }
          });
      }
    }

    if (waypoint.apCommand !== 'ForkRoute') {
      if (waypoint.apCommand === 'JoinRoute') {
        let isNext = false;
        if (waypoint.stageLabel !== 'Mission') {
          leftPanelTree.tree
            .searchByName(waypoint.branchLabel)
            .data.items.forEach((item: PlanningItem) => {
              if (item === waypoint) {
                isNext = true;
              }
              if (
                item !== waypoint &&
                isNext &&
                item.label !== 'LastWaypoint'
              ) {
                let nextWaypoint = item;
                if (nextWaypoint) {
                  layerTracks.removeLayer(trackVector.get(nextWaypoint));
                  trackVector
                    .get(nextWaypoint)
                    .setLatLngs([
                      point,
                      new L.LatLng(
                        nextWaypoint.point.latitude,
                        nextWaypoint.point.longitude,
                      ),
                    ]);
                  layerTracks.addLayer(trackVector.get(nextWaypoint));
                }
                isNext = false;
              }
            });
        } else {
          leftPanelTree.tree
            .searchByName(waypoint.stageLabel)
            .data.items.forEach((item: PlanningItem) => {
              if (item === waypoint) {
                isNext = true;
              }
              if (
                item !== waypoint &&
                isNext &&
                item.label !== 'LastWaypoint'
              ) {
                let nextWaypoint = item;
                if (nextWaypoint) {
                  layerTracks.removeLayer(trackVector.get(nextWaypoint));
                  trackVector
                    .get(nextWaypoint)
                    .setLatLngs([
                      point,
                      new L.LatLng(
                        nextWaypoint.point.latitude,
                        nextWaypoint.point.longitude,
                      ),
                    ]);
                  layerTracks.addLayer(trackVector.get(nextWaypoint));
                }
                isNext = false;
              }
            });
        }
      } else if (
        waypoint.stateTrajectory !== TrajectoryState.CreatingMissionArea &&
        waypoint.stateTrajectory !== TrajectoryState.ClosingMissionArea
      ) {
        let nextWaypoint = Array.from(waypointVector.keys())[
          Array.from(waypointVector.keys()).indexOf(waypoint) + 1
        ];
        if (nextWaypoint) {
          if (
            (!waypoint.forkLabel || !waypoint?.forkLabel.includes('Fork')) &&
            nextWaypoint.forkLabel &&
            nextWaypoint?.forkLabel.includes('Fork')
          ) {
            let isNext = false;
            if (waypoint.stageLabel !== 'Mission') {
              leftPanelTree.tree
                .searchByName(waypoint.branchLabel)
                .data.items.forEach((item: PlanningItem) => {
                  if (item === waypoint) {
                    isNext = true;
                  }
                  if (item !== waypoint && isNext) {
                    layerTracks.removeLayer(trackVector.get(item));
                    trackVector
                      .get(item)
                      .setLatLngs([
                        point,
                        trackVector.get(item).getLatLngs()[1] as L.LatLng,
                      ]);
                    layerTracks.addLayer(trackVector.get(item));
                    isNext = false;
                  }
                });
            } else {
              leftPanelTree.tree
                .searchByName(waypoint.stageLabel)
                .data.items.forEach((item: PlanningItem) => {
                  if (item === waypoint) {
                    isNext = true;
                  }
                  if (item !== waypoint && isNext) {
                    layerTracks.removeLayer(trackVector.get(item));
                    trackVector
                      .get(item)
                      .setLatLngs([
                        point,
                        trackVector.get(item).getLatLngs()[1] as L.LatLng,
                      ]);
                    layerTracks.addLayer(trackVector.get(item));
                    isNext = false;
                  }
                });
            }
          } else {
            layerTracks.removeLayer(trackVector.get(nextWaypoint));
            trackVector
              .get(nextWaypoint)
              .setLatLngs([
                point,
                new L.LatLng(
                  nextWaypoint.point.latitude,
                  nextWaypoint.point.longitude,
                ),
              ]);
            layerTracks.addLayer(trackVector.get(nextWaypoint));
          }
        }
      } else if (
        waypoint.stateTrajectory === TrajectoryState.CreatingMissionArea ||
        waypoint.stateTrajectory === TrajectoryState.ClosingMissionArea
      ) {
        const rset = regionsVector
          .get(waypoint)
          .getLatLngs()[0] as Array<L.LatLng>;
        if (this.trajectoryUtils.isInRegion(point, rset)) {
          let nextWaypoint = Array.from(waypointVector.keys())[
            Array.from(waypointVector.keys()).indexOf(waypoint) + 1
          ];
          if (nextWaypoint) {
            if (
              (!waypoint.forkLabel || !waypoint?.forkLabel.includes('Fork')) &&
              nextWaypoint.forkLabel &&
              nextWaypoint?.forkLabel.includes('Fork')
            ) {
              let isNext = false;
              if (waypoint.stageLabel !== 'Mission') {
                leftPanelTree.tree
                  .searchByName(waypoint.branchLabel)
                  .data.items.forEach((item: PlanningItem) => {
                    if (item === waypoint) {
                      isNext = true;
                    }
                    if (item !== waypoint && isNext) {
                      layerTracks.removeLayer(trackVector.get(item));
                      trackVector
                        .get(item)
                        .setLatLngs([
                          point,
                          trackVector.get(item).getLatLngs()[1] as L.LatLng,
                        ]);
                      layerTracks.addLayer(trackVector.get(item));
                      isNext = false;
                    }
                  });
              } else {
                leftPanelTree.tree
                  .searchByName(waypoint.stageLabel)
                  .data.items.forEach((item: PlanningItem) => {
                    if (item === waypoint) {
                      isNext = true;
                    }
                    if (item !== waypoint && isNext) {
                      layerTracks.removeLayer(trackVector.get(item));
                      trackVector
                        .get(item)
                        .setLatLngs([
                          point,
                          trackVector.get(item).getLatLngs()[1] as L.LatLng,
                        ]);
                      layerTracks.addLayer(trackVector.get(item));
                      isNext = false;
                    }
                  });
              }
            } else {
              layerTracks.removeLayer(trackVector.get(nextWaypoint));
              trackVector
                .get(nextWaypoint)
                .setLatLngs([
                  point,
                  new L.LatLng(
                    nextWaypoint.point.latitude,
                    nextWaypoint.point.longitude,
                  ),
                ]);
              layerTracks.addLayer(trackVector.get(nextWaypoint));
            }
          }
        }
      }
    }
  }

  obtainSelectionListFromTree(
    trajectory: AutopilotTrajectory,
  ): AutopilotCommand[] {
    // Show pop up to select initial trajectory
    let selectionList: AutopilotCommand[] = [];

    let takeoff: AutopilotCommand = trajectory.allCommandList.find(
      (command: AutopilotCommand) =>
        command.apCommand === AutopilotCommandEnum.Takeoff,
    );
    selectionList.push(this.copyCommand(takeoff));

    return selectionList;
  }

  copyCommand(command: AutopilotCommand): AutopilotCommand {
    return JSON.parse(JSON.stringify(command));
  }
  //#endregion


}
